<template lang="html">
  <v-content>
    <input
      type="file"
      style="display: none"
      multiple
      ref="selectedDocument"
      @change="onDocumentPicked"
    />

    <!-- Delete Question Confirmation Dialog -->
    <ConfirmationDialog
      v-model="deleteQuestionConfirmationDialogOpened"
      @confirm="deleteQuestion(selectedQuestion.id)"
    >
      Are you sure you want to delete the question?
    </ConfirmationDialog>

    <!-- Attach Document to Questionnaire Dialog -->
    <v-dialog
      width="550"
      v-model="attachDocumentToQuestionDialogOpened"
      @keydown.esc="attachDocumentToQuestionDialogOpened = false"
    >
      <v-card
        flat
        @drop.prevent="addFile"
        @dragover.prevent
        v-if="selectedQuestion"
      >
        <v-card-title class="title pb-0 text-xs-left">
          {{
            selectedQuestion.document_link
              ? 'Change Attached Document'
              : 'Attach Document'
          }}
        </v-card-title>
        <v-card-text>
          <v-flex class="text-xs-left">
            <v-alert :value="true" type="info" outline>
              Please select a document to attach to this question.
            </v-alert>
          </v-flex>
          <v-layout align-center class="text-xs-left">
            <v-layout
              align-center
              class="text-xs-left mb-4"
              v-if="!documentToImport"
            >
              No document selected.
              <v-spacer />
              <v-btn
                outline
                class="default-input text-capitalize primary--text"
                :disabled="documentImporting"
                @click="pickDocument"
              >
                <v-layout align-center>
                  <v-icon class="mr-2" size="20">add</v-icon>
                  <div>Select File</div>
                </v-layout>
              </v-btn>
            </v-layout>
            <v-flex class="mb-4" v-else>
              <v-layout align-center>
                <v-icon color="primary"> insert_drive_file </v-icon>
                <span class="ml-1 my-1">{{ documentToImport.name }}</span>
                <v-spacer />
                <v-icon
                  @click="documentToImport = null"
                  style="cursor: pointer"
                  color="error"
                  >cancel</v-icon
                >
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="attachDocumentToQuestion()"
            :loading="documentImporting"
            :disabled="!documentToImport"
          >
            Upload
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="
              attachDocumentToQuestionDialogOpened = false;
              documentToImport = null;
            "
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Template Link Dialog -->
    <TemplateLinkDialog
      :opened.sync="templateLinkDialogOpened"
      :selected-questionnaire="selectedQuestionnaire"
    />

    <!-- Select Template Dialog -->
    <SelectTemplateDialog
      :opened.sync="selectTemplateDialogOpened"
      :questionnaire-templates="questionnaireTemplates"
      :selected-question="selectedQuestion"
      @refresh-questionnaire-template="
        getQuestionnaireTemplate(selectedQuestionnaire.id)
      "
    />

    <!-- Send Preview To Me Dialog -->
    <v-dialog
      v-model="isSendPreviewToMeDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="600"
      persistent
      @keydown.esc="isSendPreviewToMeDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title pb-0"
          >Email me a preview of
          {{ selectedTemplateName || selectedQuestionnaire.name }}</v-card-title
        >

        <v-card-text>
          <v-flex xs12 class="text-xs-left">
            Would you like to receive a preview of this template? The preview
            will be sent to {{ currentUser.email }}.
          </v-flex>
          <br />
          <v-flex xs12 class="text-xs-left">
            The preview will be automatically deleted after 60 minutes of
            inactivity.
          </v-flex>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="sendPreviewToMe()"
            :loading="isSendingPreviewToMe"
          >
            {{ $t('Yes') }}
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="isSendPreviewToMeDialogOpened = false"
          >
            {{ $t('No') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Template Settings Dialog -->
    <v-dialog
      v-model="templateSettingsDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="600"
      persistent
      @keydown.esc="templateSettingsDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title pb-0"> Template Settings </v-card-title>
        <v-card-text>
          <v-flex xs12 class="text-xs-left">
            <div>Template Name</div>
            <v-text-field
              v-model.trim="selectedTemplateName"
              class="default-input"
              solo
              :rules="templateNameRules"
            />
          </v-flex>
          <v-flex xs12 class="text-xs-left">
            <div>Document Path</div>
            <v-alert
              :value="true"
              outline
              block
              :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
              color="info"
              class="text-xs-left mb-2"
            >
              <strong>Document Path</strong> is the folder where the documents
              will end up for this questionnaire (inside of the "Documents"
              tab). For example, if a questionnaire for this template is sent
              out for the 2024 tax year, then "{year}/{questionnaire_name}" will
              put all documents inside of the "2024/{{ selectedTemplateName }}"
              folder.
            </v-alert>
            <v-text-field
              v-model="selectedTemplateDocumentPath"
              class="default-input"
              solo
              hide-details
            />
          </v-flex>
          <v-flex xs12 class="text-xs-left mt-2">
            <v-checkbox v-model="isAnnual">
              <template slot="label">
                <v-flex xs12 class="text-s-left">
                  <span
                    style="text-align: left; color: #0a1a25; margin-bottom: 0; text-wrap: wrap;"
                  >
                    Client should only complete ONE of these per year via the
                    Template Link generator
                  </span>
                  <v-tooltip top>
                    <v-icon size="15" class="ml-1" slot="activator">
                      information
                    </v-icon>
                    <span>
                      If your client has already started on a <br />{{
                        selectedTemplateName
                      }}
                      {{ projectViewEnabled ? 'project' : 'questionnaire' }}
                      (using the<br />
                      Template Link generator) for that given year, a new one<br />
                      will <b>NOT</b> be created. Instead, the already
                      existing<br />
                      one will be used for that year.
                    </span>
                  </v-tooltip>
                </v-flex>
              </template>
            </v-checkbox>
          </v-flex>
          <v-flex xs12 class="text-xs-left mt-2">
            <div>Action Button Text</div>
            <v-text-field
              v-model.trim="selectedActionButtonText"
              class="default-input"
              solo
              hide-details
              placeholder="Default as 'Open Questionnaire'"
            />
          </v-flex>
          <v-flex v-if="showTaxSoftwareMapping">
            <v-checkbox
              hide-details
              v-model="mapToTaxSoftware"
              label="Show Mapping to Tax Software"
            />
          </v-flex>
          <v-flex
            xs12
            class="mt-4"
            v-if="
              currentUserFirm.preferences &&
                currentUserFirm.preferences.billing_enabled &&
                stripeConnected
            "
          >
            <div class="text-xs-left">
              Deposit Amount for Template Link:
            </div>
            <v-text-field
              class="default-input"
              label="Deposit Amount"
              type="number"
              v-model="selectedDepositAmount"
              solo
              hide-details
            />
          </v-flex>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="saveTemplate()"
            :loading="savingTemplateEdition"
            :disabled="isTemplateFormNotValid"
          >
            {{ $t('save') }}
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="templateSettingsDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Selection Confirmation Dialog -->
    <ConfirmationDialog
      v-model="deleteSectionConfirmationDialogOpened"
      @confirm="deleteSection(selectedSection)"
    >
      Are you sure you want to delete the section?
    </ConfirmationDialog>

    <!-- Add Section Dialog -->
    <v-dialog
      v-model="addSectionDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="500"
      persistent
      @keydown.esc="addSectionDialogOpened = false"
      @keydown.enter="addSection()"
    >
      <v-card>
        <v-card-title class="title pb-0">
          Add {{ projectViewEnabled ? 'Step' : 'Section' }}
        </v-card-title>
        <v-card-text>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="info"
            class="text-xs-left mb-2"
          >
            Do not include slashes (/) or quotation mark (") in the
            {{ projectViewEnabled ? 'step' : 'section' }} name. You can add
            conditionality after creating the section.
          </v-alert>
          <v-layout class="text-xs-left" column>
            <v-flex xs12 class="mb-4">
              <div class="grey--text text--darken-1 caption">
                {{ projectViewEnabled ? 'Step' : 'Section' }} Type
              </div>
              <v-select
                v-model="selectedSectionType"
                :items="filteredSections"
                item-text="label"
                item-value="id"
                :placeholder="
                  projectViewEnabled
                    ? 'Select a step type'
                    : 'Select a section type'
                "
                hide-details
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs12 class="mb-4">
              <div class="grey--text text--darken-1 caption">
                {{ projectViewEnabled ? 'Step' : 'Section' }} Name
              </div>
              <v-text-field
                v-model.trim="newSectionName"
                :placeholder="
                  projectViewEnabled
                    ? 'Write a step name'
                    : 'Write a section name'
                "
                hide-details
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs12 class="mb-4">
              <div class="grey--text text--darken-1 caption">
                {{ projectViewEnabled ? 'Step' : 'Section' }} Instruction
              </div>
              <v-textarea
                v-model="newSectionInstruction"
                placeholder="Provide the instruction"
                hide-details
                solo
                class="default-input"
              />
              <v-btn
                class="text-capitalize primary--text"
                @click="useDefaultSectionInstruction(newSectionInstruction)"
                outline
                :disabled="!selectedSectionType"
              >
                Use Default Instruction
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="addSection()"
            :disabled="!newSectionSavable"
          >
            Add
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="addSectionDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Section Dialog -->
    <v-dialog
      v-model="editSectionDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="500"
      persistent
      @keydown.esc="closeSectionDialog"
    >
      <v-card>
        <v-card-title class="title pb-0">
          Edit {{ projectViewEnabled ? 'Step' : 'Section' }} ({{
            selectedSection.name
          }})
        </v-card-title>
        <v-card-text>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="info"
            class="text-xs-left mb-2"
          >
            Do not include slashes (/) or quotation mark (") in the
            {{ projectViewEnabled ? 'step' : 'section' }} name.
          </v-alert>
          <v-layout column class="text-xs-left">
            <v-flex xs12 class="mb-4">
              <div class="grey--text text--darken-1 caption">
                {{ projectViewEnabled ? 'Step' : 'Section' }} Type
              </div>
              <v-select
                v-model="selectedSection.section_type"
                :items="filteredSections"
                item-text="label"
                item-value="id"
                hide-details
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs12 class="mb-4">
              <div class="grey--text text--darken-1 caption">
                {{ projectViewEnabled ? 'Step' : 'Section' }} Name
              </div>
              <v-text-field
                v-model.trim="selectedSection.name"
                :placeholder="
                  projectViewEnabled
                    ? 'Write a step name'
                    : 'Write a section name'
                "
                hide-details
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs12 class="mb-4">
              <div class="grey--text text--darken-1 caption">
                {{ projectViewEnabled ? 'Step' : 'Section' }} Instruction
              </div>
              <v-textarea
                v-model="selectedSection.instruction"
                placeholder="Provide the instruction"
                hide-details
                solo
                class="default-input"
              />
              <v-btn
                class="text-capitalize primary--text"
                @click="
                  useDefaultSectionInstruction(selectedSection.instruction)
                "
                outline
                :disabled="!selectedSection.section_type"
              >
                Use Default Instruction
              </v-btn>
            </v-flex>

            <!-- conditional section -->
            <v-layout column>
              <v-switch
                v-model="conditionalSectionOn"
                label="Triggered by question"
                hide-details
                class="default-input mt-0 pt-0 ml-2"
              />
            </v-layout>
            <v-layout v-if="conditionalSectionOn" wrap>
              <v-flex xs12 class="mb-4">
                <div class="grey--text text--darken-1 caption">Section</div>
                <v-select
                  v-model="sectionToShow"
                  :items="
                    orderedQuestionnaireSections.filter(
                      section => section.id !== selectedSection.id,
                    )
                  "
                  item-text="name"
                  return-object
                  placeholder="Select a section"
                  hide-details
                  solo
                  class="default-input"
                />
              </v-flex>
              <v-flex v-if="sectionToShow" xs12 class="mb-4">
                <div class="grey--text text--darken-1 caption">Question</div>
                <v-select
                  v-model="selectedQuestion"
                  :items="allSectionQuestionsForTrigger(sectionToShow)"
                  item-text="formattedQuestionText"
                  return-object
                  placeholder="Select a question"
                  hide-details
                  solo
                  class="default-input"
                />
              </v-flex>
            </v-layout>
            <v-layout
              class="mb-4"
              align-center
              v-if="selectedQuestion && conditionalSectionOn"
            >
              <div class="mr-2 caption">If client selects</div>
              <v-switch
                v-model="selectedSection.trigger_answer"
                :label="selectedSection.trigger_answer"
                true-value="Yes"
                false-value="No"
                hide-details
                class="default-input ma-0 pa-0 ml-2"
                v-if="selectedQuestion.question_type === 'boolean'"
              />
              <v-select
                v-model="selectedTriggerAnswer"
                :items="selectedQuestion.options"
                @change="saveSectionTriggerAnswer()"
                solo
                multiple
                class="default-input ml-2"
                label="Choose Answer"
                hide-details
                v-else-if="
                  selectedQuestion.question_type === 'multiple' ||
                    selectedQuestion.question_type === 'select'
                "
              />
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="saveSection(selectedSection)"
            :disabled="!sectionSavable"
          >
            Save
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="closeSectionDialog"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <ConfirmationDialog
      v-model="deleteTemplateConfirmationDialogOpened"
      @confirm="deleteTemplate"
      :loading="templateDeleting"
    >
      Are you sure you want to delete the template? This template is shared with
      all the firm users. Deleting this template will delete any template
      schedulers that are associated with this template.
    </ConfirmationDialog>

    <!-- Edit Question Dialog -->
    <v-dialog
      v-model="editQuestionDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="1000"
      persistent
      scrollable
      @keydown.esc="editQuestionDialogOpened = false"
    >
      <v-card v-if="editQuestionDialogOpened" style="position: relative;">
        <!-- To prevent the dragging of questions while saving -->
        <div
          v-if="questionSaving || isDraggingFollowUpQuestion"
          style="
            background-color: white;
            height: 100%;
            opacity: 0.7;
            position: absolute;
            width: 100%;
            z-index: 2;
          "
        >
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </div>

        <!-- Partial: Edit question header -->
        <v-card-title class="title pb-0"> Edit Question </v-card-title>

        <!-- Partial: Edit question content -->
        <v-card-text>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="info"
            class="text-xs-left mb-3"
          >
            Do not include slashes (/) or periods (.) for document options. If
            you edit the question or the section, last year's answers will not
            show.
          </v-alert>

          <v-layout column>
            <!-- Partial: Generic Setup -->
            <v-layout
              class="text-xs-left mb-2"
              :set="
                (allowCharNumber = ['text', 'password'].includes(
                  selectedQuestion.question_type,
                ))
              "
            >
              <v-flex class="pr-2" xs6>
                <div class="grey--text text--darken-1 caption">Section</div>
                <v-select
                  v-model="selectedQuestion.section_id"
                  :items="orderedQuestionnaireSections"
                  item-text="name"
                  item-value="id"
                  placeholder="Select a section"
                  hide-details
                  solo
                  class="default-input"
                />
              </v-flex>
              <v-flex
                class="pl-2"
                :xs4="allowCharNumber"
                :xs6="!allowCharNumber"
              >
                <div class="grey--text text--darken-1 caption">
                  Question Type
                  <v-tooltip top>
                    <v-icon size="15" class="ml-1" slot="activator">
                      information
                    </v-icon>
                    <span
                      >Only Yes/No and Multiple Choice questions are able to be
                      followed up</span
                    >
                  </v-tooltip>
                </div>
                <v-select
                  v-model="selectedQuestion.question_type"
                  :items="questionTypes"
                  item-text="text"
                  item-value="value"
                  placeholder="Select a type"
                  hide-details
                  solo
                  @change="resetSelectedQuestion(selectedQuestion)"
                  class="default-input"
                />
              </v-flex>
              <v-flex class="pl-2" xs2 v-if="allowCharNumber">
                <div class="grey--text text--darken-1 caption">
                  Number of characters
                </div>
                <v-text-field
                  v-model="selectedQuestion.number_of_characters"
                  hide-details
                  solo
                  placeholder="#"
                  type="number"
                  class="default-input"
                />
              </v-flex>
            </v-layout>

            <!-- Question -->
            <v-layout class="mb-2 text-xs-left">
              <v-flex>
                <div class="grey--text text--darken-1 caption">Question</div>
                <quill-editor
                  id="new-message-editor"
                  v-model="selectedQuestion.text"
                  :options="editorNewOptions"
                  class="ql-message-input"
                />
                <div
                  v-if="
                    isEmpty(selectedQuestion.text) && showRequiredFieldsError
                  "
                >
                  <v-alert
                    :value="true"
                    outline
                    block
                    color="error"
                    class="text-xs-left"
                  >
                    This field is required
                  </v-alert>
                </div>
              </v-flex>
            </v-layout>

            <!-- Options for upload -->
            <v-layout
              v-if="
                selectedQuestion.question_type === 'upload' &&
                  selectedQuestion.document_link
              "
            >
              <v-layout align-center>
                <v-icon color="primary"> insert_drive_file </v-icon>
                <span class="ml-1 my-1">{{
                  selectedQuestion.document_link.split('/')[2]
                }}</span>
                <v-icon
                  @click="deleteAttachedDocument()"
                  style="cursor: pointer"
                  color="error"
                  class="ml-2"
                  size="20"
                  >cancel</v-icon
                >
              </v-layout>
              <v-spacer />
              <v-btn
                @click="attachDocumentToQuestionDialogOpened = true"
                class="default-input primary--text text-capitalize"
                flat
              >
                <v-icon color="primary" size="25"> attach_file </v-icon>
                Change Attached Document
              </v-btn>
            </v-layout>

            <!-- Options for upload -->
            <v-layout
              v-if="
                selectedQuestion.question_type === 'upload' &&
                  !selectedQuestion.document_link
              "
            >
              <v-spacer />
              <v-btn
                @click="attachDocumentToQuestionDialogOpened = true"
                class="default-input primary--text text-capitalize"
                flat
              >
                <v-icon color="primary" size="25"> attach_file </v-icon>
                Attach Document
              </v-btn>
            </v-layout>

            <!-- Options for multi select and upload -->
            <v-layout
              class="mb-2"
              column
              v-if="
                selectedQuestion.question_type === 'select' ||
                  selectedQuestion.question_type === 'multiple' ||
                  selectedQuestion.question_type === 'upload'
              "
            >
              <div
                class="grey--text text--darken-1 caption text-xs-left mb-2"
                v-if="
                  selectedQuestion.question_type === 'select' ||
                    selectedQuestion.question_type === 'multiple'
                "
              >
                Options:
              </div>
              <div
                class="grey--text text--darken-1 caption text-xs-left mb-2"
                v-else
              >
                Uploadable Documents:
              </div>
              <div
                v-if="
                  showRequiredOptionCountAlert(selectedQuestion) &&
                    showRequiredFieldsError
                "
              >
                <v-alert
                  :value="true"
                  outline
                  block
                  color="error"
                  class="text-xs-left"
                >
                  You need to add at least
                  {{
                    selectedQuestion.question_type === 'select' ||
                    selectedQuestion.question_type === 'multiple'
                      ? 'two options'
                      : 'one uploadable document'
                  }}
                </v-alert>
              </div>

              <MultipleOptions
                v-if="!isEmpty(selectedQuestion.options)"
                v-model="selectedQuestion.options"
                @changed="changeOptions(selectedQuestion, ...arguments)"
                :options-editing.sync="optionsEditing"
              />
              <NewOptionForm
                :follow-up-question="selectedQuestion"
                @addStates="createStateDropdown(selectedQuestion, false)"
                @addStatesAbbr="createStateDropdown(selectedQuestion, true)"
                @addCountries="createCountryDropdown(selectedQuestion)"
                @addUSTreasuryCurrencies="
                  createUSTreasuryCurrencyDropdown(selectedQuestion)
                "
                @addCurrencies="createCurrencyDropdown(selectedQuestion)"
                @addCurrenciesWithCodes="
                  createCurrencyDropdown(selectedQuestion, true)
                "
                @add="addOption(selectedQuestion, $event)"
              />
            </v-layout>

            <!-- More options -->
            <div class="questionnaire_template__options-container">
              <v-checkbox
                class="ma-0"
                v-model="selectedQuestion.disable_not_applicable"
                hide-details
                label="Hide Not Applicable Option"
              />
              <v-checkbox
                class="ma-0"
                v-model="selectedQuestion.optional"
                hide-details
                label="Optional"
              />
              <v-checkbox
                class="ma-0"
                v-model="selectedQuestion.disable_answer_later"
                hide-details
                label="Hide Ask Later"
              />
              <v-flex
                v-if="
                  selectedQuestion.question_type === 'upload' &&
                    showPriorYearStackable
                "
              >
                <v-checkbox
                  class="ma-0"
                  v-model="selectedQuestion.stack_prior_year"
                  hide-details
                  label="Stack with Rollover Items"
                />
              </v-flex>
            </div>

            <!-- mapToTaxSoftware -->
            <v-layout class="my-2 text-xs-left" v-if="mapToTaxSoftware">
              <v-flex xs6 class="pr-2">
                <div class="grey--text text--darken-1 caption">Form</div>
                <v-select
                  :items="exportableForms"
                  v-model="selectedQuestion.mapped_form"
                  solo
                  class="default-input"
                  item-text="name"
                  item-value="name"
                  label="Choose Form"
                  clearable
                  hide-details
                />
              </v-flex>
              <v-flex
                xs6
                class="pl-2"
                v-if="
                  selectedQuestion.mapped_form &&
                    findExportableFormForSelectedForm(
                      selectedQuestion.mapped_form,
                    )
                "
              >
                <div class="grey--text text--darken-1 caption">Key</div>
                <v-select
                  v-model="selectedQuestion.mapped_key"
                  :items="
                    findExportableFormForSelectedForm(
                      selectedQuestion.mapped_form,
                    ).mapping
                  "
                  solo
                  class="default-input"
                  item-text="label"
                  item-value="key"
                  label="Choose Key"
                  required
                  clearable
                  hide-details
                />
              </v-flex>
            </v-layout>

            <!-- Render followup questions -->
            <v-layout
              column
              class="mt-2"
              v-if="canBeFollowedUp(selectedQuestion)"
            >
              <v-flex
                class="grey--text text--darken-1 subheading mb-2 text-xs-left"
              >
                Follow-up Questions:
              </v-flex>

              <draggable
                :sort="true"
                class="list-group"
                group="followUpQuestions"
                tag="ul"
                :list="getFollowUpQuestions(selectedQuestion)"
                v-bind="dragOptions"
                :move="onMove"
                @start="isDragging = true"
                @end="() => followUpQuestionMoveEnd(selectedQuestion)"
              >
                <transition-group type="transition" :name="'flip-list'">
                  <!-- List of followup questions -->
                  <v-layout
                    class="mb-3 follow-up__question-container"
                    column
                    align-left
                    v-for="(followUpQuestion, i) in getFollowUpQuestions(
                      selectedQuestion,
                    )"
                    :key="i"
                  >
                    <!-- Partial: Followup header -->
                    <div class="questionnaire_template__condition_bar">
                      <v-icon
                        class="mr-2 questionnaire_template_block__icon drag_handle"
                        style="cursor: pointer;"
                      >
                        drag_handle
                      </v-icon>
                      <div class="mr-2 caption">If client selects</div>
                      <!-- We always want these switches/selects to re-render when any value changes on the question so setting Math.random() to the key. If not, old UI states will be retained. -->
                      <v-switch
                        v-model="
                          followUpQuestion.trigger_for_follow_up_question
                        "
                        :label="followUpQuestion.trigger_for_follow_up_question"
                        :key="Math.random()"
                        true-value="Yes"
                        false-value="No"
                        hide-details
                        class="default-input mt-0 pt-0 ml-2"
                        v-if="selectedQuestion.question_type === 'boolean'"
                      />
                      <v-select
                        v-model="
                          followUpQuestion.trigger_for_follow_up_question
                        "
                        :items="selectedQuestion.options"
                        :key="Math.random()"
                        solo
                        class="default-input ml-2"
                        item-text="text"
                        item-value="value"
                        label="Choose Answer"
                        hide-details
                        v-else-if="
                          selectedQuestion.question_type === 'select' ||
                            selectedQuestion.question_type === 'multiple'
                        "
                      />
                      <div class="ml-2 caption">then ask question below.</div>
                      <v-spacer />
                      <v-btn
                        class="error--text text-none"
                        flat
                        @click="deleteFollowUpQuestion(followUpQuestion)"
                      >
                        Remove
                      </v-btn>
                    </div>
                    <div
                      v-if="
                        !followUpQuestion.trigger_for_follow_up_question &&
                          showRequiredFieldsError
                      "
                    >
                      <v-alert
                        :value="true"
                        outline
                        block
                        color="error"
                        class="text-xs-left"
                      >
                        You must select an option to trigger this question
                      </v-alert>
                    </div>
                    <!-- Partial: Question -->
                    <v-layout align-baseline>
                      <v-flex class="pr-1" align-baseline>
                        <div
                          class="grey--text text--darken-1 caption text-xs-left"
                        >
                          Follow-up Question Type
                          <v-tooltip top v-if="$vuetify.breakpoint.mdAndUp">
                            <v-icon size="15" class="ml-1" slot="activator">
                              info
                            </v-icon>
                            <span
                              >Only Yes/No and Multiple Choice questions are
                              able to be followed up</span
                            >
                          </v-tooltip>
                        </div>
                        <v-select
                          v-model="followUpQuestion.question_type"
                          :items="firstFollowUpQuestionTypes"
                          item-text="text"
                          item-value="value"
                          hide-details
                          solo
                          class="default-input quill-select-override"
                        />
                      </v-flex>
                      <v-flex class="text-xs-left pl-1 quill-select-container">
                        <div
                          class="grey--text text--darken-1 caption text-xs-left"
                        >
                          Question
                        </div>
                        <quill-editor
                          id="followup-question-message-editor"
                          v-model="followUpQuestion.text"
                          :options="editorNewOptions"
                          class="ql-message-input"
                        />
                        <div
                          v-if="
                            isEmpty(followUpQuestion.text) &&
                              showRequiredFieldsError
                          "
                        >
                          <v-alert
                            :value="true"
                            outline
                            block
                            color="error"
                            class="text-xs-left"
                          >
                            This field is required
                          </v-alert>
                        </div>
                      </v-flex>
                    </v-layout>

                    <!-- Partial: Options for text or password -->
                    <v-layout>
                      <v-flex
                        v-if="
                          followUpQuestion.question_type === 'text' ||
                            followUpQuestion.question_type === 'password'
                        "
                        xs4
                        class="pr-1"
                      >
                        <v-layout>
                          <v-flex class="text-xs-left" xs12>
                            <div class="grey--text text--darken-1 caption">
                              Number of characters
                            </div>
                            <v-text-field
                              v-model="followUpQuestion.number_of_characters"
                              hide-details
                              solo
                              placeholder="#"
                              type="number"
                              class="default-input"
                            />
                          </v-flex>
                        </v-layout>
                      </v-flex>
                    </v-layout>

                    <!-- Partial: Options for select multipe or upload -->
                    <v-layout
                      class="mt-2 mb-4"
                      column
                      v-if="
                        followUpQuestion.question_type === 'select' ||
                          followUpQuestion.question_type === 'multiple' ||
                          followUpQuestion.question_type === 'upload'
                      "
                    >
                      <div
                        class="grey--text text--darken-1 caption text-xs-left mb-2"
                        v-if="
                          followUpQuestion.question_type === 'select' ||
                            followUpQuestion.question_type === 'multiple'
                        "
                      >
                        Options:
                      </div>
                      <div
                        class="grey--text text--darken-1 caption text-xs-left mb-2"
                        v-else
                      >
                        Uploadable Documents:
                      </div>
                      <div
                        v-if="
                          showRequiredOptionCountAlert(followUpQuestion) &&
                            showRequiredFieldsError
                        "
                      >
                        <v-alert
                          :value="true"
                          outline
                          block
                          color="error"
                          class="text-xs-left"
                        >
                          You need to add at least
                          {{
                            followUpQuestion.question_type === 'select' ||
                            followUpQuestion.question_type === 'multiple'
                              ? 'two options'
                              : 'one uploadable document'
                          }}
                        </v-alert>
                      </div>
                      <MultipleOptions
                        v-if="!isEmpty(followUpQuestion.options)"
                        v-model="followUpQuestion.options"
                        @changed="changeOptions(followUpQuestion, ...arguments)"
                        :options-editing.sync="optionsEditing"
                      />
                      <NewOptionForm
                        :follow-up-question="followUpQuestion"
                        @addStates="
                          createStateDropdown(followUpQuestion, false)
                        "
                        @addStatesAbbr="
                          createStateDropdown(followUpQuestion, true)
                        "
                        @addCountries="createCountryDropdown(followUpQuestion)"
                        @addUSTreasuryCurrencies="
                          createUSTreasuryCurrencyDropdown(followUpQuestion)
                        "
                        @addCurrencies="
                          createCurrencyDropdown(followUpQuestion)
                        "
                        @addCurrenciesWithCodes="
                          createCurrencyDropdown(followUpQuestion, true)
                        "
                        @add="addOption(followUpQuestion, $event)"
                      />
                    </v-layout>

                    <!-- Partial: Template options -->
                    <div class="questionnaire_template__options-container">
                      <v-checkbox
                        v-model="followUpQuestion.disable_not_applicable"
                        hide-details
                        class="ma-0"
                        label="Hide Not Applicable Option"
                      />
                      <v-checkbox
                        class="ma-0"
                        v-model="followUpQuestion.optional"
                        hide-details
                        label="Optional"
                      />
                      <v-checkbox
                        v-model="followUpQuestion.disable_answer_later"
                        hide-details
                        class="ma-0"
                        label="Hide Ask Later"
                      />
                      <v-flex
                        v-if="
                          followUpQuestion.question_type === 'upload' &&
                            showPriorYearStackable
                        "
                      >
                        <v-checkbox
                          class="ma-0"
                          v-model="followUpQuestion.stack_prior_year"
                          hide-details
                          label="Stack with Rollover Items"
                        />
                      </v-flex>
                    </div>

                    <!-- Partial: mapToTaxSoftware -->
                    <v-layout class="my-2 text-xs-left" v-if="mapToTaxSoftware">
                      <v-flex xs6 class="pr-2">
                        <div class="grey--text text--darken-1 caption">
                          Form
                        </div>
                        <v-select
                          :items="exportableForms"
                          v-model="followUpQuestion.mapped_form"
                          solo
                          class="default-input"
                          item-text="name"
                          item-value="name"
                          label="Choose Form"
                          clearable
                          hide-details
                        />
                      </v-flex>
                      <v-flex
                        xs6
                        class="pl-2"
                        v-if="
                          followUpQuestion.mapped_form &&
                            findExportableFormForSelectedForm(
                              followUpQuestion.mapped_form,
                            )
                        "
                      >
                        <div class="grey--text text--darken-1 caption">Key</div>
                        <v-select
                          v-model="followUpQuestion.mapped_key"
                          :items="
                            findExportableFormForSelectedForm(
                              followUpQuestion.mapped_form,
                            ).mapping
                          "
                          solo
                          class="default-input"
                          item-text="label"
                          item-value="key"
                          label="Choose Key"
                          required
                          clearable
                          hide-details
                        />
                      </v-flex>
                    </v-layout>

                    <!-- Partial: Options for special entries -->
                    <v-layout
                      v-else-if="isProtectedClientEntry(followUpQuestion)"
                      class="py-4"
                    >
                      <v-icon slot="activator" class="darkgrey--text"
                        >lock</v-icon
                      >
                      <span class="ml-1"
                        >You cannot edit this question's entries. Please reach
                        out to support if you want to change the entries</span
                      >
                    </v-layout>

                    <!-- Partial: Options for entries -->
                    <v-layout
                      column
                      v-else-if="followUpQuestion.question_type === 'entries'"
                    >
                      <v-flex
                        class="grey--text text--darken-1 subheading mb-2 text-xs-left"
                      >
                        Entry Inputs
                      </v-flex>
                      <v-layout
                        class="mb-2 ml-2"
                        column
                        align-left
                        v-for="(entryInput, i) in getFollowUpQuestions(
                          followUpQuestion,
                        )"
                        :key="i"
                      >
                        <v-layout align-center class="mb-2 text-xs-left">
                          <span class="mr-2 title grey--text text--darken-1"
                            >{{ i + 1 }}.</span
                          >
                          <v-flex xs4 class="pr-2">
                            <div class="grey--text text--darken-1 caption">
                              Input Type
                            </div>
                            <v-select
                              v-model="entryInput.question_type"
                              :items="entryInputTypes"
                              item-text="text"
                              item-value="value"
                              placeholder="Question Type"
                              hide-details
                              solo
                              class="default-input"
                            />
                          </v-flex>
                          <v-flex xs8 class="pl-2">
                            <div class="grey--text text--darken-1 caption">
                              Input Name
                            </div>
                            <v-text-field
                              v-model="entryInput.text"
                              placeholder="Question"
                              hide-details
                              solo
                              class="default-input"
                            />
                          </v-flex>
                          <v-btn
                            class="error--text text-none"
                            flat
                            @click="deleteFollowUpQuestion(entryInput)"
                          >
                            Remove
                          </v-btn>
                        </v-layout>
                        <v-layout
                          class="mb-2 ml-4"
                          column
                          v-if="
                            entryInput.question_type === 'select' ||
                              entryInput.question_type === 'multiple' ||
                              entryInput.question_type === 'upload'
                          "
                        >
                          <div
                            class="grey--text text--darken-1 caption text-xs-left mb-1"
                          >
                            {{
                              entryInput.question_type === 'upload'
                                ? 'Document '
                                : ''
                            }}Options:
                          </div>
                          <MultipleOptions
                            v-if="!isEmpty(entryInput.options)"
                            v-model="entryInput.options"
                            use-letter
                            :options-editing.sync="optionsEditing"
                          />
                          <NewOptionForm
                            :follow-up-question="entryInput"
                            @addStates="createStateDropdown(entryInput, false)"
                            @addStatesAbbr="
                              createStateDropdown(entryInput, true)
                            "
                            @addCountries="createCountryDropdown(entryInput)"
                            @addUSTreasuryCurrencies="
                              createUSTreasuryCurrencyDropdown(entryInput)
                            "
                            @addCurrencies="createCurrencyDropdown(entryInput)"
                            @addCurrenciesWithCodes="
                              createCurrencyDropdown(entryInput, true)
                            "
                            @add="addOption(entryInput, $event)"
                          />
                        </v-layout>
                      </v-layout>
                      <v-btn
                        @click="addFollowUpQuestion(followUpQuestion)"
                        class="default-input primary--text text-capitalize"
                        flat
                      >
                        <v-icon color="primary" size="25"> add </v-icon>
                        Add Entry Input
                      </v-btn>
                    </v-layout>

                    <!-- Render additional followup questions -->
                    <v-layout column v-if="canBeFollowedUp(followUpQuestion)">
                      <v-layout
                        class="mt-4 grey--text text--darken-1 subheading text-xs-left"
                        v-if="getFollowUpQuestions(followUpQuestion).length"
                      >
                        Additional Follow-up Questions:
                      </v-layout>
                      <draggable
                        :sort="true"
                        class="list-group"
                        group="additionalFollowUpQuestions"
                        tag="ul"
                        :list="getFollowUpQuestions(followUpQuestion)"
                        v-bind="dragOptions"
                        :move="onMove"
                        @start="isDragging = true"
                        @end="() => followUpQuestionMoveEnd(followUpQuestion)"
                      >
                        <transition-group type="transition" :name="'flip-list'">
                          <!-- List of additional followup questions -->
                          <v-layout
                            class="mt-3 additional-follow-up__question-container"
                            column
                            align-left
                            v-for="(secondFollowUpQuestion,
                            i) in getFollowUpQuestions(followUpQuestion)"
                            :key="i"
                          >
                            <div class="questionnaire_template__condition_bar">
                              <v-icon
                                class="mr-2 questionnaire_template_block__icon drag_handle"
                                style="cursor: pointer;"
                              >
                                drag_handle
                              </v-icon>
                              <div class="mr-2 caption">If client selects</div>
                              <!-- We always want these switches/selects to re-render when any value changes on the question so setting Math.random() to the key. If not, old UI states will be retained. -->
                              <v-switch
                                v-model="
                                  secondFollowUpQuestion.trigger_for_follow_up_question
                                "
                                :label="
                                  secondFollowUpQuestion.trigger_for_follow_up_question
                                "
                                :key="Math.random()"
                                true-value="Yes"
                                false-value="No"
                                hide-details
                                class="default-input mt-0 pt-0"
                                v-if="
                                  followUpQuestion.question_type === 'boolean'
                                "
                              />
                              <v-select
                                v-model="
                                  secondFollowUpQuestion.trigger_for_follow_up_question
                                "
                                :items="followUpQuestion.options"
                                :key="Math.random()"
                                solo
                                class="default-input"
                                item-text="text"
                                item-value="value"
                                label="Choose Answer"
                                hide-details
                                v-else-if="
                                  followUpQuestion.question_type === 'select' ||
                                    followUpQuestion.question_type ===
                                      'multiple'
                                "
                              />
                              <div class="ml-2 caption">
                                then ask question below.
                              </div>
                              <v-spacer />

                              <v-btn
                                class="error--text text-none"
                                flat
                                @click="
                                  deleteFollowUpQuestion(secondFollowUpQuestion)
                                "
                              >
                                Remove
                              </v-btn>
                            </div>
                            <div
                              v-if="
                                !secondFollowUpQuestion.trigger_for_follow_up_question &&
                                  showRequiredFieldsError
                              "
                            >
                              <v-alert
                                :value="true"
                                outline
                                block
                                color="error"
                                class="text-xs-left"
                              >
                                You must select an option to trigger this
                                question
                              </v-alert>
                            </div>

                            <v-layout align-baseline>
                              <v-flex class="mb-2 pr-1" align-baseline>
                                <div
                                  class="grey--text text--darken-1 caption text-xs-left"
                                >
                                  Follow-up Question Type
                                </div>
                                <v-select
                                  v-model="secondFollowUpQuestion.question_type"
                                  class="default-input quill-select-override"
                                  solo
                                  :items="secondFollowUpQuestionTypes"
                                  item-text="text"
                                  item-value="value"
                                  label="Type"
                                  hide-details
                                />
                              </v-flex>
                              <v-flex class="mb-2 pl-1 quill-select-container">
                                <div
                                  class="grey--text text--darken-1 caption text-xs-left"
                                >
                                  Question
                                </div>
                                <quill-editor
                                  id="additional-followup-question-message-editor"
                                  v-model="secondFollowUpQuestion.text"
                                  :options="editorNewOptions"
                                  class="ql-message-input"
                                />
                                <div
                                  v-if="
                                    isEmpty(secondFollowUpQuestion.text) &&
                                      showRequiredFieldsError
                                  "
                                >
                                  <v-alert
                                    :value="true"
                                    outline
                                    block
                                    color="error"
                                    class="text-xs-left"
                                  >
                                    This field is required
                                  </v-alert>
                                </div>
                              </v-flex>
                            </v-layout>

                            <v-layout
                              class="mt-2 mb-4"
                              column
                              v-if="
                                secondFollowUpQuestion.question_type ===
                                  'select' ||
                                  secondFollowUpQuestion.question_type ===
                                    'multiple' ||
                                  secondFollowUpQuestion.question_type ===
                                    'upload'
                              "
                            >
                              <div
                                class="grey--text text--darken-1 caption text-xs-left mb-2"
                                v-if="
                                  secondFollowUpQuestion.question_type ===
                                    'select' ||
                                    secondFollowUpQuestion.question_type ===
                                      'multiple'
                                "
                              >
                                Options:
                              </div>
                              <div
                                class="grey--text text--darken-1 caption text-xs-left mb-2"
                                v-else
                              >
                                Uploadable Documents:
                              </div>
                              <div
                                v-if="
                                  showRequiredOptionCountAlert(
                                    secondFollowUpQuestion,
                                  ) && showRequiredFieldsError
                                "
                              >
                                <v-alert
                                  :value="true"
                                  outline
                                  block
                                  color="error"
                                  class="text-xs-left"
                                >
                                  You need to add at least
                                  {{
                                    secondFollowUpQuestion.question_type ===
                                      'select' ||
                                    secondFollowUpQuestion.question_type ===
                                      'multiple'
                                      ? 'two options'
                                      : 'one uploadable document'
                                  }}
                                </v-alert>
                              </div>
                              <MultipleOptions
                                v-if="!isEmpty(secondFollowUpQuestion.options)"
                                v-model="secondFollowUpQuestion.options"
                                use-letter
                                @changed="
                                  changeOptions(
                                    secondFollowUpQuestion,
                                    ...arguments,
                                  )
                                "
                                :options-editing.sync="optionsEditing"
                              />
                              <NewOptionForm
                                :follow-up-question="secondFollowUpQuestion"
                                @addStates="
                                  createStateDropdown(
                                    secondFollowUpQuestion,
                                    false,
                                  )
                                "
                                @addStatesAbbr="
                                  createStateDropdown(
                                    secondFollowUpQuestion,
                                    true,
                                  )
                                "
                                @addCountries="
                                  createCountryDropdown(secondFollowUpQuestion)
                                "
                                @addCurrencies="
                                  createCurrencyDropdown(secondFollowUpQuestion)
                                "
                                @addCurrenciesWithCodes="
                                  createCurrencyDropdown(
                                    secondFollowUpQuestion,
                                    true,
                                  )
                                "
                                @addUSTreasuryCurrencies="
                                  createUSTreasuryCurrencyDropdown(
                                    secondFollowUpQuestion,
                                  )
                                "
                                @add="addOption(secondFollowUpQuestion, $event)"
                              />
                            </v-layout>

                            <div
                              class="questionnaire_template__options-container"
                            >
                              <v-checkbox
                                v-model="
                                  secondFollowUpQuestion.disable_not_applicable
                                "
                                hide-details
                                class="ma-0"
                                label="Hide Not Applicable Option"
                              />
                              <v-checkbox
                                v-model="secondFollowUpQuestion.optional"
                                hide-details
                                class="ma-0"
                                label="Optional"
                              />
                              <v-checkbox
                                v-model="
                                  secondFollowUpQuestion.disable_answer_later
                                "
                                hide-details
                                class="ma-0"
                                label="Hide Ask Later"
                              />
                              <v-flex
                                v-if="
                                  secondFollowUpQuestion.question_type ===
                                    'upload' && showPriorYearStackable
                                "
                              >
                                <v-checkbox
                                  class="ma-0"
                                  v-model="
                                    secondFollowUpQuestion.stack_prior_year
                                  "
                                  hide-details
                                  label="Stack with Rollover Items"
                                />
                              </v-flex>
                            </div>
                          </v-layout>
                        </transition-group>
                      </draggable>

                      <!-- Add Additional Follow-up Question -->
                      <sx-button-group
                        :is-full-width="true"
                        x-align="right"
                        class="mt-3"
                      >
                        <v-tooltip
                          bottom
                          v-if="!followUpQuestionSavable(followUpQuestion)"
                        >
                          <div slot="activator">
                            <sx-button
                              type="secondary"
                              size="tiny"
                              :is-disabled="true"
                            >
                              <sx-icon size="16" name="add" />
                              Add Additional Follow-up Question
                            </sx-button>
                          </div>
                          <span
                            >Please check the previous question and make sure it
                            is savable</span
                          >
                        </v-tooltip>
                        <div
                          v-else
                          @click="addFollowUpQuestion(followUpQuestion)"
                        >
                          <sx-button type="secondary" size="tiny">
                            <sx-icon size="16" name="add" />
                            Add Additional Follow-up Question
                          </sx-button>
                        </div>
                      </sx-button-group>
                    </v-layout>
                    <!-- /Render additional followup questions -->
                  </v-layout>
                </transition-group>
              </draggable>

              <!-- Add Follow-up Question -->
              <sx-button-group x-align="center" :is-full-width="true">
                <div @click="addFollowUpQuestion(selectedQuestion)">
                  <sx-button type="secondary" size="small">
                    <sx-icon size="16" name="add" />
                    Add Follow-up Question
                  </sx-button>
                </div>
              </sx-button-group>
            </v-layout>
            <!-- /Render followup questions -->

            <!-- Options: "Fixed" entries -->
            <v-layout
              v-else-if="isProtectedClientEntry(selectedQuestion)"
              class="py-4"
            >
              <v-icon slot="activator" class="darkgrey--text">lock</v-icon>
              <span class="ml-1"
                >You cannot edit this question's entries. Please reach out to
                support if you want to change the entries</span
              >
            </v-layout>

            <!-- Options: Entries -->
            <v-layout
              column
              v-else-if="selectedQuestion.question_type === 'entries'"
            >
              <v-flex
                class="grey--text text--darken-1 subheading mb-2 text-xs-left"
              >
                Entry Inputs
              </v-flex>
              <v-layout
                class="mb-2 ml-2"
                column
                align-left
                v-for="(entryInput, i) in getFollowUpQuestions(
                  selectedQuestion,
                )"
                :key="i"
              >
                <v-layout align-center class="mb-2 text-xs-left">
                  <span class="mr-2 title grey--text text--darken-1"
                    >{{ i + 1 }}.</span
                  >
                  <v-flex xs4 class="pr-2">
                    <div class="grey--text text--darken-1 caption">
                      Input Type
                    </div>
                    <v-select
                      v-model="entryInput.question_type"
                      :items="entryInputTypes"
                      item-text="text"
                      item-value="value"
                      placeholder="Question Type"
                      hide-details
                      solo
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs8 class="pl-2">
                    <div class="grey--text text--darken-1 caption">
                      Input Name
                    </div>
                    <v-text-field
                      v-model="entryInput.text"
                      placeholder="Question"
                      hide-details
                      solo
                      class="default-input"
                    />
                  </v-flex>
                  <v-btn
                    class="error--text text-none"
                    flat
                    @click="deleteFollowUpQuestion(entryInput)"
                  >
                    Remove
                  </v-btn>
                </v-layout>
                <v-layout
                  class="mb-2 ml-4"
                  column
                  v-if="
                    entryInput.question_type === 'select' ||
                      entryInput.question_type === 'multiple' ||
                      entryInput.question_type === 'upload'
                  "
                >
                  <div
                    class="grey--text text--darken-1 caption text-xs-left mb-1"
                  >
                    {{
                      entryInput.question_type === 'upload' ? 'Document ' : ''
                    }}Options:
                  </div>
                  <MultipleOptions
                    v-if="!isEmpty(entryInput.options)"
                    v-model="entryInput.options"
                    use-letter
                    :options-editing.sync="optionsEditing"
                  />
                  <NewOptionForm
                    :follow-up-question="entryInput"
                    @addStates="createStateDropdown(entryInput, false)"
                    @addStatesAbbr="createStateDropdown(entryInput, true)"
                    @addCountries="createCountryDropdown(entryInput)"
                    @addUSTreasuryCurrencies="
                      createUSTreasuryCurrencyDropdown(entryInput)
                    "
                    @addCurrencies="createCurrencyDropdown(entryInput)"
                    @addCurrenciesWithCodes="
                      createCurrencyDropdown(entryInput, true)
                    "
                    @add="addOption(entryInput, $event)"
                  />
                </v-layout>
              </v-layout>
              <v-btn
                @click="addFollowUpQuestion(selectedQuestion)"
                class="default-input primary--text text-capitalize"
                flat
              >
                <v-icon color="primary" size="25"> add </v-icon>
                Add Entry Input
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-text>

        <!-- Partial: Edit Question footer -->
        <v-card-actions>
          <sx-button-group :is-full-width="true" x-align="right">
            <div @click="closeQuestionDialogsAndResetQuestionnaireQuestions()">
              <sx-button type="secondary">
                {{ $t('cancel') }}
              </sx-button>
            </div>
            <div
              @click="trySavingQuestion(selectedQuestion)"
              v-if="editQuestionDialogOpened"
            >
              <sx-button
                type="primary"
                :is-loading="questionSaving || isDraggingFollowUpQuestion"
              >
                <sx-icon size="16" name="check" />
                <span>Save</span>
              </sx-button>
            </div>
          </sx-button-group>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Question Dialog -->
    <v-dialog
      v-model="createQuestionDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="1000"
      persistent
      scrollable
      @keydown.esc="createQuestionDialogOpened = false"
    >
      <v-card v-if="createQuestionDialogOpened">
        <!-- Partial: Create question header -->
        <v-card-title class="title pb-0"> Create Question </v-card-title>

        <!-- Partial: Create question content -->
        <v-card-text>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="info"
            class="text-xs-left mb-3"
          >
            Do not include slashes (/) or periods (.) for document options. For
            client entries as a follow-up or for second follow up questions,
            first save the question and click "Edit".
          </v-alert>
          <v-layout column>
            <!-- Partial: CharNumber options -->
            <v-layout
              class="text-xs-left mb-2"
              :set="
                (allowCharNumber = ['text', 'password'].includes(
                  selectedQuestion.question_type,
                ))
              "
            >
              <v-flex class="pr-2" xs6>
                <div class="grey--text text--darken-1 caption">Section</div>
                <v-select
                  v-model="selectedQuestion.section_id"
                  :items="orderedQuestionnaireSections"
                  item-text="name"
                  item-value="id"
                  placeholder="Select a section"
                  hide-details
                  solo
                  class="default-input"
                />
              </v-flex>

              <v-flex
                class="pl-2"
                :xs4="allowCharNumber"
                :xs6="!allowCharNumber"
              >
                <div class="grey--text text--darken-1 caption">
                  Question Type
                  <v-tooltip top>
                    <v-icon size="15" class="ml-1" slot="activator">
                      information
                    </v-icon>
                    <span
                      >Only Yes/No and Multiple Choice questions are able to be
                      followed up</span
                    >
                  </v-tooltip>
                </div>
                <v-select
                  v-model="selectedQuestion.question_type"
                  :items="questionTypes"
                  item-text="text"
                  item-value="value"
                  placeholder="Select a type"
                  hide-details
                  solo
                  @change="resetSelectedQuestion(selectedQuestion)"
                  class="default-input"
                />
              </v-flex>

              <v-flex class="pl-2" xs2 v-if="allowCharNumber">
                <div class="grey--text text--darken-1 caption">
                  Number of characters
                </div>
                <v-text-field
                  v-model="selectedQuestion.number_of_characters"
                  hide-details
                  solo
                  placeholder="#"
                  type="number"
                  class="default-input"
                />
              </v-flex>
            </v-layout>

            <!-- Partial: Question -->
            <v-layout class="text-xs-left mb-2">
              <v-flex xs12>
                <div class="grey--text text--darken-1 caption">Question</div>
                <quill-editor
                  id="edit-message"
                  v-model="selectedQuestion.text"
                  :options="editorEditOptions"
                  class="ql-message-input"
                />
                <div
                  v-if="
                    isEmpty(selectedQuestion.text) && showRequiredFieldsError
                  "
                >
                  <v-alert
                    :value="true"
                    outline
                    block
                    color="error"
                    class="text-xs-left"
                  >
                    This field is required
                  </v-alert>
                </div>
              </v-flex>
            </v-layout>

            <!-- Partial: Template Options -->
            <div class="questionnaire_template__options-container">
              <v-checkbox
                class="ma-0"
                v-model="selectedQuestion.disable_not_applicable"
                hide-details
                label="Hide Not Applicable Option"
              />
              <v-checkbox
                class="ma-0"
                v-model="selectedQuestion.optional"
                hide-details
                label="Optional"
              />
              <v-checkbox
                class="ma-0"
                v-model="selectedQuestion.disable_answer_later"
                hide-details
                label="Hide Ask Later"
              />
              <v-flex v-if="selectedQuestion.question_type === 'upload'">
                <v-checkbox
                  class="ma-0"
                  v-model="selectedQuestion.stack_prior_year"
                  hide-details
                  label="Stack with Rollover Items"
                />
              </v-flex>
            </div>

            <!-- Partial: options for mapToTaxSoftware -->
            <v-layout class="my-4 text-xs-left" v-if="mapToTaxSoftware">
              <v-flex xs6 class="pr-2">
                <div class="grey--text text--darken-1 caption">Form</div>
                <v-select
                  :items="exportableForms"
                  v-model="selectedQuestion.mapped_form"
                  solo
                  class="default-input"
                  item-text="name"
                  item-value="name"
                  hide-details
                  label="Choose Form"
                  clearable
                />
              </v-flex>
              <v-flex
                xs6
                class="pl-2"
                v-if="
                  selectedQuestion.mapped_form &&
                    findExportableFormForSelectedForm(
                      selectedQuestion.mapped_form,
                    )
                "
              >
                <div class="grey--text text--darken-1 caption">Key</div>
                <v-select
                  v-model="selectedQuestion.mapped_key"
                  :items="
                    findExportableFormForSelectedForm(
                      selectedQuestion.mapped_form,
                    ).mapping
                  "
                  class="default-input"
                  solo
                  item-text="label"
                  item-value="key"
                  label="Choose Key"
                  hide-details
                  clearable
                />
              </v-flex>
            </v-layout>

            <!-- Partial: Options for select, multiple or upload -->
            <v-layout
              class="mt-2 mb-4"
              column
              v-if="
                selectedQuestion.question_type === 'select' ||
                  selectedQuestion.question_type === 'multiple' ||
                  selectedQuestion.question_type === 'upload'
              "
            >
              <div
                class="grey--text text--darken-1 caption text-xs-left mb-2"
                v-if="
                  selectedQuestion.question_type === 'select' ||
                    selectedQuestion.question_type === 'multiple'
                "
              >
                Options:
              </div>
              <div
                class="grey--text text--darken-1 caption text-xs-left mb-2"
                v-else
              >
                Uploadable Documents:
              </div>
              <div
                v-if="
                  showRequiredOptionCountAlert(selectedQuestion) &&
                    showRequiredFieldsError
                "
              >
                <v-alert
                  :value="true"
                  outline
                  block
                  color="error"
                  class="text-xs-left"
                >
                  You need to add at least
                  {{
                    selectedQuestion.question_type === 'select' ||
                    selectedQuestion.question_type === 'multiple'
                      ? 'two options'
                      : 'one uploadable document'
                  }}
                </v-alert>
              </div>
              <MultipleOptions
                v-if="!isEmpty(selectedQuestion.options)"
                v-model="selectedQuestion.options"
                @changed="changeOptions(selectedQuestion, ...arguments)"
                :options-editing.sync="optionsEditing"
              />
              <NewOptionForm
                :follow-up-question="selectedQuestion"
                @addStates="createStateDropdown(selectedQuestion, false)"
                @addStatesAbbr="createStateDropdown(selectedQuestion, true)"
                @addCountries="createCountryDropdown(selectedQuestion)"
                @addUSTreasuryCurrencies="
                  createUSTreasuryCurrencyDropdown(selectedQuestion)
                "
                @addCurrencies="createCurrencyDropdown(selectedQuestion)"
                @addCurrenciesWithCodes="
                  createCurrencyDropdown(selectedQuestion, true)
                "
                @add="addOption(selectedQuestion, $event)"
              />
            </v-layout>

            <!-- Render follow up questions -->
            <v-layout column v-if="canBeFollowedUp(selectedQuestion)">
              <v-flex
                class="grey--text text--darken-1 subheading mb-2 text-xs-left"
              >
                Follow-up Questions:
              </v-flex>

              <!-- List of follow up questions -->
              <v-layout
                class="mb-2 follow-up__question-container"
                column
                align-left
                v-for="(followUpQuestion, i) in getFollowUpQuestions(
                  selectedQuestion,
                )"
                :key="i"
              >
                <!-- Partial: Question header -->
                <div class="questionnaire_template__condition_bar">
                  <div class="mr-2 caption">If client selects</div>
                  <v-switch
                    v-model="followUpQuestion.trigger_for_follow_up_question"
                    :label="followUpQuestion.trigger_for_follow_up_question"
                    true-value="Yes"
                    false-value="No"
                    hide-details
                    class="default-input mt-0 pt-0 ml-2"
                    v-if="selectedQuestion.question_type === 'boolean'"
                  />
                  <v-select
                    v-model="followUpQuestion.trigger_for_follow_up_question"
                    :items="selectedQuestion.options"
                    solo
                    class="default-input ml-2"
                    item-text="text"
                    item-value="value"
                    label="Choose Answer"
                    hide-details
                    v-else-if="
                      selectedQuestion.question_type === 'select' ||
                        selectedQuestion.question_type === 'multiple'
                    "
                  />
                  <div class="ml-2 caption">then ask question below.</div>
                  <v-spacer />
                  <v-btn
                    class="error--text text-none"
                    flat
                    @click="deleteFollowUpQuestion(followUpQuestion)"
                  >
                    Remove
                  </v-btn>
                </div>

                <div
                  v-if="
                    !followUpQuestion.trigger_for_follow_up_question &&
                      showRequiredFieldsError
                  "
                >
                  <v-alert
                    :value="true"
                    outline
                    block
                    color="error"
                    class="text-xs-left"
                  >
                    You must select an option to trigger this question
                  </v-alert>
                </div>

                <!-- Partial: Question content -->
                <v-layout class="text-xs-left mb-4">
                  <v-flex xs4 class="pr-2">
                    <div class="grey--text text--darken-1 caption">
                      Question Type
                    </div>
                    <v-select
                      v-model="followUpQuestion.question_type"
                      :items="secondFollowUpQuestionTypes"
                      item-text="text"
                      item-value="value"
                      placeholder="Question Type"
                      hide-details
                      solo
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs8 class="pl-2">
                    <div class="grey--text text--darken-1 caption">
                      Question
                    </div>
                    <quill-editor
                      id="new-message-editor"
                      v-model="followUpQuestion.text"
                      :options="editorCreateOptions"
                      class="ql-message-input"
                    />
                    <div
                      v-if="
                        isEmpty(followUpQuestion.text) &&
                          showRequiredFieldsError
                      "
                    >
                      <v-alert
                        :value="true"
                        outline
                        block
                        color="error"
                        class="text-xs-left"
                      >
                        This field is required
                      </v-alert>
                    </div>
                  </v-flex>
                </v-layout>

                <!-- Partial: 2nd level options mapToTaxSoftware -->
                <v-layout class="my-2 text-xs-left" v-if="mapToTaxSoftware">
                  <v-flex xs6 class="pr-2">
                    <div class="grey--text text--darken-1 caption">Form</div>
                    <v-select
                      :items="exportableForms"
                      v-model="followUpQuestion.mapped_form"
                      solo
                      class="default-input"
                      item-text="name"
                      return-object
                      hide-details
                      label="Choose Form"
                      clearable
                    />
                  </v-flex>
                  <v-flex
                    xs6
                    class="pl-2"
                    v-if="
                      followUpQuestion.mapped_form &&
                        findExportableFormForSelectedForm(
                          followUpQuestion.mapped_form,
                        )
                    "
                  >
                    <div class="grey--text text--darken-1 caption">Key</div>
                    <v-select
                      v-model="followUpQuestion.mapped_key"
                      :items="
                        findExportableFormForSelectedForm(
                          followUpQuestion.mapped_form,
                        ).mapping
                      "
                      class="default-input"
                      item-text="label"
                      item-value="key"
                      label="Choose Key"
                      hide-details
                      clearable
                      solo
                    />
                  </v-flex>
                </v-layout>

                <!-- Partial: 2nd level options for text and password -->
                <v-layout>
                  <v-flex
                    v-if="
                      followUpQuestion.question_type === 'text' ||
                        followUpQuestion.question_type === 'password'
                    "
                    xs4
                    class="pr-1"
                  >
                    <v-layout>
                      <v-flex class="text-xs-left" xs12>
                        <div class="grey--text text--darken-1 caption">
                          Number of characters
                        </div>
                        <v-text-field
                          v-model="followUpQuestion.number_of_characters"
                          hide-details
                          solo
                          placeholder="#"
                          type="number"
                          class="default-input"
                        />
                      </v-flex>
                    </v-layout>
                  </v-flex>

                  <!-- Partial: Template Options -->
                  <div class="questionnaire_template__options-container">
                    <v-checkbox
                      class="ma-0"
                      v-model="followUpQuestion.disable_not_applicable"
                      hide-details
                      label="Hide Not Applicable Option"
                    />
                    <v-checkbox
                      class="ma-0"
                      v-model="followUpQuestion.optional"
                      hide-details
                      label="Optional"
                    />
                    <v-checkbox
                      class="ma-0"
                      v-model="followUpQuestion.disable_answer_later"
                      hide-details
                      label="Hide Ask Later"
                    />
                    <v-flex
                      v-if="
                        followUpQuestion.question_type === 'upload' &&
                          showPriorYearStackable
                      "
                    >
                      <v-checkbox
                        class="ma-0"
                        v-model="followUpQuestion.stack_prior_year"
                        hide-details
                        label="Stack with Rollover Items"
                      />
                    </v-flex>
                  </div>
                </v-layout>

                <!-- Partial: 2nd level options for select multiple or upload -->
                <v-layout
                  class="mt-2 mb-4"
                  column
                  v-if="
                    followUpQuestion.question_type === 'select' ||
                      followUpQuestion.question_type === 'multiple' ||
                      followUpQuestion.question_type === 'upload'
                  "
                >
                  <div
                    class="grey--text text--darken-1 caption text-xs-left mb-2"
                    v-if="followUpQuestion.question_type === 'select'"
                  >
                    Options:
                  </div>
                  <div
                    class="grey--text text--darken-1 caption text-xs-left mb-2"
                    v-else
                  >
                    Uploadable Documents:
                  </div>
                  <div
                    v-if="
                      isEmpty(followUpQuestion.options) &&
                        showRequiredFieldsError
                    "
                  >
                    <v-alert
                      :value="true"
                      outline
                      block
                      color="error"
                      class="text-xs-left"
                    >
                      You need to add at least
                      {{
                        followUpQuestion.question_type === 'select' ||
                        followUpQuestion.question_type === 'multiple'
                          ? 'two options'
                          : 'one uploadable document'
                      }}
                    </v-alert>
                  </div>
                  <MultipleOptions
                    v-if="!isEmpty(followUpQuestion.options)"
                    v-model="followUpQuestion.options"
                    @changed="changeOptions(followUpQuestion, ...arguments)"
                    :options-editing.sync="optionsEditing"
                  />
                  <NewOptionForm
                    :follow-up-question="followUpQuestion"
                    @addStates="createStateDropdown(followUpQuestion, false)"
                    @addStatesAbbr="createStateDropdown(followUpQuestion, true)"
                    @addCountries="createCountryDropdown(followUpQuestion)"
                    @addUSTreasuryCurrencies="
                      createUSTreasuryCurrencyDropdown(followUpQuestion)
                    "
                    @addCurrencies="createCurrencyDropdown(followUpQuestion)"
                    @addCurrenciesWithCodes="
                      createCurrencyDropdown(followUpQuestion, true)
                    "
                    @add="addOption(followUpQuestion, $event)"
                  />
                </v-layout>

                <!-- Partial: 2nd level options for entries -->
                <v-layout
                  column
                  v-else-if="followUpQuestion.question_type === 'entries'"
                >
                  <v-flex
                    class="grey--text text--darken-1 subheading mb-2 text-xs-left"
                  >
                    Entry Inputs
                  </v-flex>
                  <v-layout
                    class="mb-2 ml-2"
                    column
                    align-left
                    v-for="(entryOption, i) in followUpQuestion.entryOptions"
                    :key="i"
                  >
                    <v-layout align-center class="mb-2 text-xs-left">
                      <span class="mr-2 title grey--text text--darken-1"
                        >{{ i + 1 }}.</span
                      >
                      <v-flex xs4 class="pr-2">
                        <div class="grey--text text--darken-1 caption">
                          Input Type
                        </div>
                        <v-select
                          v-model="entryOption.type"
                          :items="entryInputTypes"
                          item-text="text"
                          item-value="value"
                          placeholder="Question Type"
                          hide-details
                          solo
                          class="default-input"
                        />
                      </v-flex>
                      <v-flex xs8 class="pl-2">
                        <div class="grey--text text--darken-1 caption">
                          Input Name
                        </div>
                        <v-text-field
                          v-model="entryOption.text"
                          placeholder="Question"
                          hide-details
                          solo
                          class="default-input"
                        />
                      </v-flex>
                      <v-btn
                        class="error--text text-none"
                        flat
                        @click="
                          deleteEntryOption(followUpQuestion, entryOption)
                        "
                      >
                        Remove
                      </v-btn>
                    </v-layout>

                    <v-layout
                      class="mb-2 ml-4"
                      column
                      v-if="
                        entryOption.type === 'select' ||
                          entryOption.type === 'multiple' ||
                          entryOption.type === 'upload'
                      "
                    >
                      <div
                        class="grey--text text--darken-1 caption text-xs-left mb-1"
                      >
                        {{
                          entryOption.type === 'upload' ? 'Document ' : ''
                        }}Options:
                      </div>
                      <v-layout
                        align-center
                        class="mb-2 ml-4 text-xs-left"
                        :key="i"
                        v-for="(option, i) in entryOption.options"
                      >
                        {{ toLetters(i + 1) }}. {{ option }}
                        <v-icon
                          color="error"
                          style="cursor: pointer"
                          size="20"
                          class="ml-2"
                          @click="entryOption.options.splice(i, 1)"
                        >
                          cancel
                        </v-icon>
                      </v-layout>
                      <NewOptionForm
                        :follow-up-question="entryOption"
                        @addStates="createStateDropdown(entryOption, false)"
                        @addStatesAbbr="createStateDropdown(entryOption, true)"
                        @addCountries="createCountryDropdown(entryOption)"
                        @addCurrencies="createCurrencyDropdown(entryOption)"
                        @addCurrenciesWithCodes="
                          createCurrencyDropdown(entryOption, true)
                        "
                        @addUSTreasuryCurrencies="
                          createUSTreasuryCurrencyDropdown(entryOption)
                        "
                        @add="addEntryOption(entryOption, $event)"
                      />
                    </v-layout>
                  </v-layout>
                  <v-btn
                    @click="addEntryItem(followUpQuestion)"
                    class="default-input primary--text text-capitalize"
                    flat
                  >
                    <v-icon color="primary" size="25"> add </v-icon>
                    Add Entry Input
                  </v-btn>
                </v-layout>
              </v-layout>
              <v-tooltip bottom v-if="!questionSavable(selectedQuestion)">
                <v-btn
                  slot="activator"
                  class="default-input primary--text text-capitalize"
                  flat
                  disabled
                >
                  <v-icon color="primary" size="25"> add </v-icon>
                  Add Follow-up Question
                </v-btn>
                <span
                  >Please check the previous question and make sure it is
                  savable</span
                >
              </v-tooltip>
              <v-btn
                v-else
                @click="addFollowUpQuestion(selectedQuestion)"
                class="default-input primary--text text-capitalize"
                flat
              >
                <v-icon color="primary" size="25"> add </v-icon>
                Add Follow-up Question
              </v-btn>
            </v-layout>
            <!-- /Render follow up questions -->

            <!-- Entries -->
            <v-layout
              column
              v-else-if="selectedQuestion.question_type === 'entries'"
            >
              <v-flex
                class="grey--text text--darken-1 subheading mb-2 text-xs-left"
              >
                Entry Inputs
              </v-flex>
              <v-layout
                class="mb-2 ml-2"
                column
                align-left
                v-for="(entryInput, i) in getFollowUpQuestions(
                  selectedQuestion,
                )"
                :key="i"
              >
                <v-layout align-center class="mb-2 text-xs-left">
                  <span class="mr-2 title grey--text text--darken-1"
                    >{{ i + 1 }}.</span
                  >
                  <v-flex xs4 class="pr-2">
                    <div class="grey--text text--darken-1 caption">
                      Input Type
                    </div>
                    <v-select
                      v-model="entryInput.question_type"
                      :items="entryInputTypes"
                      item-text="text"
                      item-value="value"
                      placeholder="Question Type"
                      hide-details
                      solo
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs8 class="pl-2">
                    <div class="grey--text text--darken-1 caption">
                      Input Name
                    </div>
                    <v-text-field
                      autofocus="true"
                      v-model="entryInput.text"
                      placeholder="Question"
                      hide-details
                      solo
                      class="default-input"
                    />
                  </v-flex>
                  <v-btn
                    class="error--text text-none"
                    flat
                    @click="deleteFollowUpQuestion(entryInput)"
                  >
                    Remove
                  </v-btn>
                </v-layout>
                <v-layout
                  class="mb-2 ml-4"
                  column
                  v-if="
                    entryInput.question_type === 'select' ||
                      entryInput.question_type === 'multiple'
                  "
                >
                  <div
                    class="grey--text text--darken-1 caption text-xs-left mb-1"
                    v-if="
                      entryInput.question_type === 'select' ||
                        entryInput.question_type === 'multiple'
                    "
                  >
                    Options:
                  </div>
                  <MultipleOptions
                    v-if="!isEmpty(entryInput.options)"
                    v-model="entryInput.options"
                    use-letter
                    :options-editing.sync="optionsEditing"
                  />
                  <NewOptionForm
                    :follow-up-question="entryInput"
                    @addStates="createStateDropdown(entryInput, false)"
                    @addStatesAbbr="createStateDropdown(entryInput, true)"
                    @addCountries="createCountryDropdown(entryInput)"
                    @addUSTreasuryCurrencies="
                      createUSTreasuryCurrencyDropdown(entryInput)
                    "
                    @addCurrencies="createCurrencyDropdown(entryInput)"
                    @addCurrenciesWithCodes="
                      createCurrencyDropdown(entryInput, true)
                    "
                    @add="addOption(entryInput, $event)"
                  />
                </v-layout>
              </v-layout>
              <v-btn
                @click="addFollowUpQuestion(selectedQuestion)"
                class="default-input primary--text text-capitalize"
                flat
              >
                <v-icon color="primary" size="25"> add </v-icon>
                Add Entry Input
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-text>

        <!-- Partial: Create question footer -->
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize default-input primary"
            @click="trySavingQuestion(selectedQuestion)"
            v-if="createQuestionDialogOpened"
            :loading="questionSaving || isDraggingFollowUpQuestion"
          >
            Save
          </v-btn>
          <v-btn
            outline
            class="text-capitalize default-input error--text"
            @click="closeQuestionDialogsAndResetQuestionnaireQuestions()"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Export Template Dialog -->
    <v-dialog
      v-model="exportTemplateDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="600"
      @keydown.esc="exportTemplateDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title class="title">
          Export {{ selectedQuestionnaire.name }} Template
        </v-card-title>
        <v-card-text>
          <v-layout column v-if="selectedQuestionnaire">
            <v-select
              v-model="selectedFormat"
              :items="['PDF', 'Spreadsheet']"
              placeholder="Select a format"
              hide-details
              solo
              class="default-input"
            />
            <div class="questionnaire-templates-container">
              <v-checkbox
                v-for="(section, i) in selectedQuestionnaire.sections.map(
                  a => a.name,
                )"
                :key="i"
                v-model="sectionsToInclude[i]"
                :label="section"
              />
            </div>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <sx-button-group :is-full-width="true" x-align="right">
            <div @click="exportTemplateDialogOpened = false">
              <sx-button type="secondary">
                {{ $t('cancel') }}
              </sx-button>
            </div>
            <div @click="exportTemplate()">
              <sx-button type="primary" :is-disabled="!selectedFormat">
                Export
              </sx-button>
            </div>
          </sx-button-group>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Questionnaire Customization Dialog -->
    <questionnaire-customization-dialog
      :opened.sync="templateCustomizationDialogOpened"
      :selected-questionnaire="selectedQuestionnaire"
      @refresh-questionnaires="
        getQuestionnaireTemplate(selectedQuestionnaire.id)
      "
    />

    <!-- Questionnaire Preview Dialog -->
    <QuestionnairePreview
      v-model="showPreview"
      :questionnaire="selectedQuestionnaire"
    />

    <!-- Questionnaire Preview Dialog -->
    <QuestionnaireReactPreview
      v-model="showReactPreview"
      :templateId="selectedQuestionnaire.id"
    />

    <!-- Partial: Header -->
    <v-layout class="mb-2" align-center>
      <sc-flex
        :is-y-centered="true"
        :gap="4"
        v-if="isEmbeddedInNext && isInIframe"
      >
        <sc-h1>Templates</sc-h1>
      </sc-flex>

      <sc-flex :is-y-centered="true" :gap="4" v-else>
        <sc-button-group>
          <sc-button
            type="silent"
            icon-left="arrow-left"
            size="small"
            :is-icon-only="true"
            @click="$router.push('/')"
            >Back</sc-button
          >
        </sc-button-group>
        <sc-h1>
          {{ projectViewEnabled ? 'Project' : 'Questionnaire' }} Templates
        </sc-h1>
      </sc-flex>
    </v-layout>

    <!-- Partial: Content -->
    <v-card class="content-container">
      <!-- State: Loading -->
      <v-card-text v-if="loading">
        <v-progress-circular
          indeterminate
          size="50"
          class="primary--text ma-5"
        />
      </v-card-text>

      <!-- State: Content -->
      <v-card-text v-else>
        <v-layout column>
          <!-- Partial: Tabs -->
          <!-- <v-tabs
            v-model="active"
            color="white"
            slider-color="primary"
            class="mb-2"
            show-arrows
            active-class="font-weight-bold"
          >
            <v-tab key="newTemplate" ripple class="text-none">
              <v-icon size="18"> add </v-icon>
              Create a template
            </v-tab>
            <v-tab
              v-for="questionnaire in questionnaireTemplates"
              :key="questionnaire.id"
              ripple
              class="text-none"
            >
              {{ questionnaire.name }}
            </v-tab>
          </v-tabs> -->

          <div class="questionnaire_templates__mainContainer">
            <!-- Search Bar -->
            <sc-flex :gap="4" :is-between="true">
              <v-text-field
                v-model="searchQuery"
                label="Search Templates"
                class="default-input subheading"
                solo
                clearable
                hide-details
                style="max-width: 400px"
              ></v-text-field>
              <div class="questionnaire_templates__templateItemSticky">
                <sc-button-group>
                  <sc-button @click="active = 0" size="small" type="secondary">
                    <sc-icon name="add" size="small"></sc-icon>
                    Create a template</sc-button
                  >
                </sc-button-group>
              </div>
            </sc-flex>

            <!-- Container for Items -->
            <div class="questionnaire_templates__templateItemContainer">
              <div
                v-for="(questionnaire, index) in filteredTemplates"
                class="questionnaire_templates__templateItem"
                :key="index + 1"
                :class="
                  active === index + 1
                    ? 'questionnaire_templates__templateItemSelected'
                    : ''
                "
                @click="selectFilteredQuestionnaire(questionnaire.id)"
              >
                <sc-small>{{ questionnaire.name }}</sc-small>
              </div>
              <div
                v-if="!filteredTemplates.length"
                class="questionnaire_templates__templateItemEmpty"
              >
                <sc-small>Not found</sc-small>
              </div>
            </div>
          </div>

          <!-- State: Templates are loading -->
          <v-layout v-if="templateLoading" column align-center>
            <v-progress-circular
              indeterminate
              size="50"
              class="primary--text ma-5"
            />
          </v-layout>

          <!-- State: Templates are ready -->
          <v-layout v-else column>
            <!-- State: Editing the selected questionnaire -->
            <sc-flex
              v-if="active !== 0 && !isEmpty(questionnaireTemplates)"
              :is-y-centered="true"
              :is-between="true"
              class="mb-2"
            >
              <sc-h5>{{ selectedQuestionnaire.name }}</sc-h5>
              <sc-button-group>
                <sc-button
                  type="ghost"
                  size="small"
                  @click.prevent.stop="isSendPreviewToMeDialogOpened = true"
                  >Send Me a Test</sc-button
                >
                <template>
                  <div>
                    <v-tooltip bottom v-if="isEmbeddedInNext">
                      <v-template slot="activator">
                        <sc-button
                          type="ghost"
                          size="small"
                          @click.prevent.stop="getReactPreview()"
                        >
                          Show Preview
                        </sc-button>
                      </v-template>
                      <span
                        >In order to use this feature, you will need to be
                        logged into app.soraban.com</span
                      >
                    </v-tooltip>
                    <sc-button
                      v-else
                      type="ghost"
                      size="small"
                      @click.prevent.stop="getPreview()"
                    >
                      Show Preview
                    </sc-button>
                  </div>
                </template>
                <sc-button
                  type="ghost"
                  size="small"
                  @click="templateLinkDialogOpened = true"
                  >Create Template Link</sc-button
                >
                <sc-button
                  :data-onboarding="isOnboarding"
                  type="ghost"
                  size="small"
                  @click="templateCustomizationDialogOpened = true"
                  >Edit Messages</sc-button
                >
                <sc-button
                  type="ghost"
                  size="small"
                  icon-left="gear"
                  @click="openTemplateSettingsDialog()"
                  :with-bottom-tooltip="true"
                  :is-icon-only="true"
                  >Settings</sc-button
                >
              </sc-button-group>
            </sc-flex>

            <!-- State: Creating new template -->
            <div v-else>
              <div class="text-xs-left">New template name</div>
              <div :data-onboarding="canCreateTemplate ? 'true' : 'false'">
                <v-text-field
                  v-model.trim="selectedQuestionnaire.name"
                  class="default-input"
                  solo
                  placeholder="e.g. Annual Tax Planning"
                  :rules="templateNameRules"
                />
              </div>
            </div>

            <!-- State: List of questions in a selected questionnaire -->
            <v-expansion-panel
              class="questionnaire_template_block mb-4"
              v-if="
                active !== 0 && selectedQuestionnaire.questionnaire_questions
              "
              :value="0"
            >
              <v-expansion-panel-content>
                <!-- Partial: Question list header -->
                <template v-slot:header>
                  <div class="questionnaire-expansion-header">
                    <div class="client-title font-weight-bold">
                      {{ projectViewEnabled ? 'Steps' : 'Sections' }}
                      <v-tooltip bottom>
                        <v-icon size="15" class="ml-1" slot="activator">
                          information
                        </v-icon>
                        <span
                          >Click to each
                          {{ projectViewEnabled ? 'step' : 'section' }} to
                          expand</span
                        >
                      </v-tooltip>
                    </div>
                  </div>
                </template>

                <!-- Partial: Question list content -->
                <v-card class="expansion-card__content" flat>
                  <draggable
                    :sort="true"
                    class="list-group px-4"
                    group="orderedSections"
                    :list="orderedSections"
                    tag="ul"
                    v-bind="dragOptions"
                    :move="onMove"
                    @start="isDragging = true"
                    @end="
                      isDragging = false;
                      sectionMoveEnd();
                    "
                  >
                    <v-expansion-panel
                      class="elevation-0 mb-3"
                      v-for="section in orderedSections"
                      :key="section.order"
                    >
                      <v-expansion-panel-content>
                        <div slot="header" class="expansion-panel__header">
                          <v-layout row align-center>
                            <v-icon
                              class="mr-2 questionnaire_template_block__icon drag_handle"
                            >
                              drag_handle
                            </v-icon>
                            {{ section.name }}
                            <v-chip
                              small
                              class="mr-2 question-chip"
                              v-if="section.trigger_question_id"
                              >Conditional</v-chip
                            >
                            <v-chip
                              small
                              class="mr-2 question-chip"
                              v-if="sectionKey(section) === 'questionnaire'"
                              >{{ sortedQuestions(section).length }}</v-chip
                            >
                            <v-icon v-if="section.locked">lock</v-icon>
                          </v-layout>
                        </div>
                        <v-card class="expansion-card__content" flat>
                          <v-layout
                            column
                            v-if="sectionKey(section) === 'questionnaire'"
                          >
                            <draggable
                              :sort="true"
                              class="list-group"
                              group="questions"
                              tag="ul"
                              :list="sortedQuestions(section)"
                              v-bind="dragOptions"
                              :move="onMove"
                              @start="isDragging = true"
                              @end="
                                isDragging = false;
                                questionMoveEnd();
                              "
                            >
                              <transition-group
                                type="transition"
                                :name="'flip-list'"
                              >
                                <v-card
                                  class="list-group-item"
                                  v-for="question in sortedQuestions(section)"
                                  :key="question.id"
                                  flat
                                  @dblclick="openEditQuestionDialog(question)"
                                >
                                  <v-layout>
                                    <v-layout column>
                                      <v-layout
                                        align-center
                                        class="grey--text text--darken-2 py-2"
                                      >
                                        <v-icon
                                          class="mr-2 questionnaire_template_block__icon drag_handle"
                                        >
                                          drag_handle
                                        </v-icon>
                                        <div
                                          class="questionnaire-template-glossarybox questionnaire-template-glossarybox--top"
                                        >
                                          <sx-html :text="question.text" />
                                        </div>
                                        <v-chip
                                          small
                                          class="mr-2 question-chip"
                                          v-if="
                                            questionIsNotTriggerableByRolloverItem(
                                              question,
                                            )
                                          "
                                          >Untriggerable</v-chip
                                        >
                                        <v-spacer />

                                        <v-flex shrink class="ml-2 py-2">
                                          <v-layout align-center>
                                            <v-tooltip bottom class="mr-1">
                                              <i
                                                slot="activator"
                                                class="material-icons-outlined light-gray--text"
                                                style="cursor: pointer"
                                                @click="
                                                  openDuplicateQuestionDialog(
                                                    question,
                                                  )
                                                "
                                              >
                                                copy
                                              </i>
                                              <span>Copy</span>
                                            </v-tooltip>
                                            <v-tooltip bottom class="mx-1">
                                              <i
                                                slot="activator"
                                                class="material-icons-outlined light-gray--text"
                                                style="cursor: pointer"
                                                @click="
                                                  openEditQuestionDialog(
                                                    question,
                                                  )
                                                "
                                              >
                                                edit
                                              </i>
                                              <span>Edit</span>
                                            </v-tooltip>
                                            <v-tooltip bottom class="ml-1">
                                              <i
                                                slot="activator"
                                                class="material-icons-outlined error--text"
                                                style="cursor: pointer"
                                                @click="
                                                  openDeleteQuestionConfirmationDialog(
                                                    question,
                                                  )
                                                "
                                              >
                                                delete
                                              </i>
                                              <span>Delete</span>
                                            </v-tooltip>
                                          </v-layout>
                                        </v-flex>
                                      </v-layout>

                                      <!-- Entries -->
                                      <v-layout
                                        wrap
                                        class="ml-2"
                                        align-start
                                        v-if="
                                          question.question_type === 'entries'
                                        "
                                      >
                                        <v-icon class="mr-2" size="25"
                                          >subdirectory_arrow_right</v-icon
                                        >
                                        <sc-flex :is-wrapping="true" :gap="4">
                                          <sc-question-label
                                            v-for="(followUpQuestion,
                                            i) in getFollowUpQuestions(
                                              question,
                                              i,
                                            )"
                                            :key="i"
                                          >
                                            {{
                                              limitString(
                                                followUpQuestion.text,
                                                120,
                                              )
                                            }}
                                          </sc-question-label>
                                        </sc-flex>
                                      </v-layout>

                                      <!-- Followup questions -->
                                      <v-layout
                                        class="ml-2 questionnaire-template-question"
                                        column
                                        v-else-if="
                                          !isEmpty(
                                            getFollowUpQuestions(question),
                                          )
                                        "
                                        :key="trigger"
                                        v-for="trigger in getTriggers(
                                          getFollowUpQuestions(question),
                                        )"
                                      >
                                        <v-layout align-start>
                                          <v-flex shrink>
                                            <v-layout>
                                              <div
                                                class="questionnaire-template-line"
                                              >
                                                <div
                                                  class="questionnaire-template-line__icon"
                                                >
                                                  <v-icon size="20"
                                                    >subdirectory_arrow_right</v-icon
                                                  >
                                                </div>
                                                <div
                                                  class="questionnaire-template-line__option"
                                                >
                                                  {{
                                                    truncateString(trigger, 40)
                                                  }}
                                                </div>
                                                <div
                                                  class="questionnaire-template-line__icon"
                                                >
                                                  <v-icon size="20"
                                                    >arrow_right_alt</v-icon
                                                  >
                                                </div>
                                              </div>
                                            </v-layout>
                                          </v-flex>
                                          <v-layout column>
                                            <v-layout
                                              column
                                              v-for="(followUpQuestion,
                                              i) in getFollowUpQuestionsWithTrigger(
                                                question,
                                                trigger,
                                              )"
                                              :key="i"
                                              class="mb-3 grey--text text--darken-2"
                                            >
                                              <v-layout align-center>
                                                <div
                                                  class="questionnaire-template-glossarybox"
                                                >
                                                  <sx-html
                                                    :text="
                                                      followUpQuestion.text
                                                    "
                                                  />
                                                </div>
                                                <v-spacer />
                                              </v-layout>
                                              <v-layout
                                                wrap
                                                class="ml-2"
                                                align-start
                                                v-if="
                                                  followUpQuestion.question_type ===
                                                    'entries'
                                                "
                                              >
                                                <div
                                                  class="questionnaire-template-line"
                                                >
                                                  <div
                                                    class="questionnaire-template-line"
                                                  >
                                                    <v-icon size="24"
                                                      >subdirectory_arrow_right</v-icon
                                                    >
                                                  </div>
                                                  <v-chip
                                                    v-for="(secondFollowUpQuestion,
                                                    i) in getFollowUpQuestions(
                                                      followUpQuestion,
                                                      i,
                                                    )"
                                                    :key="i"
                                                    outline
                                                    class="primary--text mt-2 question-chip"
                                                    small
                                                    >{{
                                                      secondFollowUpQuestion.text
                                                    }}</v-chip
                                                  >
                                                </div>
                                              </v-layout>
                                              <v-layout
                                                v-else-if="
                                                  !isEmpty(
                                                    getFollowUpQuestions(
                                                      followUpQuestion,
                                                    ),
                                                  )
                                                "
                                                v-for="(secondFollowUpQuestion,
                                                i) in getFollowUpQuestions(
                                                  followUpQuestion,
                                                )"
                                                :key="i"
                                                class="mt-2 grey--text text--darken-2"
                                                align-start
                                              >
                                                <div
                                                  class="questionnaire-template-line"
                                                >
                                                  <div
                                                    class="questionnaire-template-line__icon"
                                                  >
                                                    <v-icon size="20"
                                                      >subdirectory_arrow_right</v-icon
                                                    >
                                                  </div>

                                                  <div
                                                    class="questionnaire-template-line__option"
                                                  >
                                                    {{
                                                      truncateString(
                                                        secondFollowUpQuestion.trigger_for_follow_up_question,
                                                        40,
                                                      )
                                                    }}
                                                  </div>
                                                  <div
                                                    class="questionnaire-template-line__icon"
                                                  >
                                                    <v-icon size="20"
                                                      >arrow_right_alt</v-icon
                                                    >
                                                  </div>
                                                </div>

                                                <div
                                                  class="questionnaire-template-glossarybox"
                                                >
                                                  <sx-html
                                                    :text="
                                                      secondFollowUpQuestion.text
                                                    "
                                                  />
                                                </div>

                                                <v-spacer />
                                              </v-layout>
                                            </v-layout>
                                          </v-layout>
                                        </v-layout>
                                      </v-layout>
                                    </v-layout>
                                  </v-layout>
                                </v-card>
                              </transition-group>
                            </draggable>
                          </v-layout>
                          <v-layout
                            column
                            v-else-if="
                              sectionKey(section) === 'custom' ||
                                sectionKey(section) === 'agreement_signature'
                            "
                          >
                            <!-- Editor -->
                            <v-layout v-if="section.editing">
                              <quill-editor
                                id="edit-custom-text"
                                v-model="section.custom_element"
                                :options="
                                  sectionKey(section) === 'agreement_signature'
                                    ? editorAgreementSignatureOptions
                                    : editorNewOptions
                                "
                                class="ql-message-input ql-message-input--long"
                              />
                            </v-layout>
                            <v-layout
                              v-else-if="section.custom_element"
                              class="questionnaire_templates_custom_section"
                            >
                              <sx-html :text="section.custom_element" />
                            </v-layout>
                            <v-layout
                              column
                              align-center
                              v-else-if="isEmpty(section.custom_element)"
                              class="questionnaire_templates_custom_section"
                              >Empty</v-layout
                            >

                            <!-- Controls -->
                            <v-layout
                              class="questionnaire_templates_custom_section__controls"
                            >
                              <sx-button-group
                                :is-full-width="true"
                                x-align="right"
                              >
                                <div
                                  v-if="section.editing"
                                  @click="saveSection(section)"
                                >
                                  <sx-button type="secondary" size="small">
                                    <span>Save</span>
                                  </sx-button>
                                </div>
                                <div v-else @click="section.editing = true">
                                  <sx-button type="secondary" size="small">
                                    <sx-icon :size="16" name="edit" />
                                    <span>Edit</span>
                                  </sx-button>
                                </div>
                              </sx-button-group>
                            </v-layout>
                          </v-layout>
                          <v-layout class="questionnaire-editor__footer">
                            <!-- New Section -->
                            <div v-if="sectionKey(section) === 'questionnaire'">
                              <sx-button-group x-align="left">
                                <div @click="openCreateQuestionDialog(section)">
                                  <sx-button type="primary" size="small">
                                    <sx-icon :size="20" name="add" />
                                    New Question
                                  </sx-button>
                                </div>
                              </sx-button-group>
                            </div>
                            <div v-else />
                            <!-- Edit current section -->
                            <sx-button-group x-align="right">
                              <div @click="openEditSectionDialog(section)">
                                <sx-button type="secondary" size="tiny">
                                  <sx-icon :size="14" name="edit" />
                                  Edit
                                  {{ projectViewEnabled ? 'Step' : 'Section' }}
                                </sx-button>
                              </div>
                              <div @click="openDeleteSectionDialog(section)">
                                <sx-button type="error" size="tiny">
                                  <sx-icon :size="14" name="trash" />
                                  Delete
                                  {{ projectViewEnabled ? 'Step' : 'Section' }}
                                </sx-button>
                              </div>
                            </sx-button-group>
                          </v-layout>
                        </v-card>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </draggable>
                </v-card>

                <!-- Partial: Question list footer -->
                <v-layout align-center class="pb-4">
                  <sx-button-group :is-full-width="true" x-align="center">
                    <div
                      @click="openAddSectionDialog()"
                      :data-onboarding="
                        selectedQuestionnaire.sections &&
                        selectedQuestionnaire.sections.length > 0
                          ? 'false'
                          : 'true'
                      "
                    >
                      <sx-button type="secondary" size="small">
                        <sx-icon name="add" :size="20" />
                        <span
                          >Add
                          {{ projectViewEnabled ? 'Step' : 'Section' }}</span
                        >
                      </sx-button>
                    </div>
                  </sx-button-group>
                </v-layout>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- State: Annual question toggle -->
            <div v-if="active === 0">
              <v-checkbox v-model="selectedQuestionnaire.is_annual">
                <template slot="label">
                  Annual
                  {{ projectViewEnabled ? 'Project' : 'Questionnaire' }}
                  <v-tooltip top>
                    <v-icon size="15" class="ml-1" slot="activator">
                      information
                    </v-icon>
                    <span
                      >Check this if the
                      {{ projectViewEnabled ? 'project' : 'questionnaire' }}
                      is only sent out once per year.</span
                    >
                  </v-tooltip>
                </template>
              </v-checkbox>
            </div>
          </v-layout>

          <!-- Partial: Create actions -->
          <v-layout>
            <sc-button-group
              v-if="active === 0 || isEmpty(questionnaireTemplates)"
              align="center"
            >
              <sc-button
                :data-onboarding="canCreateTemplate ? 'false' : 'true'"
                @click="createTemplate()"
                type="secondary"
                :is-disabled="canCreateTemplate"
                >Create Template</sc-button
              >
            </sc-button-group>
            <template v-else>
              <v-btn
                outline
                class="primary--text text-capitalize"
                @click="exportTemplateDialogOpened = true"
              >
                Export Template
              </v-btn>
              <v-btn
                class="primary text-capitalize"
                @click="duplicateTemplate()"
              >
                Duplicate Template
              </v-btn>
              <v-btn
                outline
                class="error--text text-capitalize"
                @click="deleteTemplateConfirmationDialogOpened = true"
              >
                Delete Template
              </v-btn>
            </template>
          </v-layout>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-content>
</template>

<script>
import { getEditorOptions } from '@/constants/editor';
import { mapGetters } from 'vuex';
import { statesRecord } from '@/constants/variables';
import {
  countriesRecord,
  currenciesRecord,
  usTreasuryForeignCurrenciesRecord,
} from '@/constants/countries-currencies';
import API from '@/api';
import cloneDeep from 'lodash/cloneDeep';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import draggable from 'vuedraggable';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import MultipleOptions from '@/components/MultipleOptions';
import NewOptionForm from './components/NewOptionForm';
import QuestionnaireCustomizationDialog from '@/components/dialogs/QuestionnaireCustomizationDialog';
import QuestionnaireMixin from '@/mixins/QuestionnaireMixin';
import UtilsMixins from '@/mixins/UtilsMixins';
import QuestionnairePreview from './components/QuestionnairePreview';
import QuestionnaireReactPreview from './components/QuestionnaireReactPreview';
import SelectTemplateDialog from '@/components/dialogs/SelectTemplateDialog';
import SxButton from '@/components/sx/SxButton';
import SxButtonGroup from '@/components/sx/SxButtonGroup';
import SxHtml from '@/components/sx/SxHtml';
import SxFlex from '@/components/sx/SxFlex';
import SxIcon from '@/components/sx/SxIcon';
import TemplateLinkDialog from '@/components/dialogs/TemplateLinkDialog';
import union from 'lodash/union';

const DEFAULT_ENGAGEMENT_LETTER =
  'Dear {signer_names}:\n<br/><br/>\nThank you for choosing {firm_name} to assist you with your taxes. This letter confirms the terms of our engagement with you and outlines the nature and extent of the services we will provide.\n<br/><br/>\nWe will review your current federal and state income tax returns. We will depend on you to provide the information we need to complete an accurate assessment and provide recommendations. We may ask you to clarify some items but will not audit or otherwise verify the data you submit. Review or revision of prior year(s) returns is also available at an additional charge.\n<br/><br/>\nWe will perform services only as needed to review, revise or prepare your tax returns. Our work will not include procedures to find misappropriated funds or other irregularities. Accordingly, our engagement should not be relied upon to disclose errors, fraud, or other illegal acts, though it may be necessary for you to clarify some of the information you submit. We will, of course, inform you of any material errors, fraud, or other illegal acts we discover.\n<br/><br/>\nThe law imposes penalties when taxpayers underestimate their tax liability. Please call us if you have concerns about such penalties.\n<br/><br/>\nShould we encounter instances of unclear tax law, or of potential conflicts in the interpretation of the law, we will outline the reasonable courses of action and the risks and consequences of each. We will ultimately adopt, on your behalf, the alternative you select.\n<br/><br/>\nOur fee will be based on the time required at the standard hourly billing rate plus any out-of-pocket expenses. Invoices are due and payable upon presentation. To the extent permitted by state law, an interest charge may be added to all accounts not paid within thirty (30) days.\n<br/><br/>\nWe will return your original records to you at the end of this engagement. You should securely store these records, along with all supporting documents, canceled checks, etc., as these items may later be needed to prove accuracy and completeness of a return. We will retain copies of your records and our work papers for your engagement for seven years, after which these documents will be destroyed.\n<br/><br/>\nOur engagement to prepare your tax returns will conclude with the delivery of our analysis of your return along with any recommended clarifications or corrections. If you have not selected to e-file your returns with our office, you will be solely responsible to file the returns with the appropriate taxing authorities. Review all tax-return documents carefully before signing them.\n<br/><br/>\nTo affirm that this letter correctly summarizes your understanding of the arrangements for this work, please electronically sign this letter in the space indicated.';

export default {
  name: 'QuestionnaireTemplates',
  components: {
    ConfirmationDialog,
    draggable,
    MultipleOptions,
    NewOptionForm,
    QuestionnaireCustomizationDialog,
    QuestionnairePreview,
    QuestionnaireReactPreview,
    SelectTemplateDialog,
    SxButton,
    SxButtonGroup,
    SxHtml,
    SxIcon,
    TemplateLinkDialog,
  },
  mixins: [QuestionnaireMixin, FirmPreferenceMixins, UtilsMixins],
  data() {
    return {
      requiredFieldsRules: [v => !!v || 'This field is required'],
      showRequiredFieldsError: false,
      optionsEditing: false,
      selectTemplateDialogOpened: false,
      allowCharNumber: false,
      active: 1,
      drag: false,
      showPreview: false,
      showReactPreview: false,
      editTemplateName: false,
      questionnaireTemplates: [],
      editQuestionDialogOpened: false,
      createQuestionDialogOpened: false,
      deleteQuestionConfirmationDialogOpened: false,
      deleteTemplateConfirmationDialogOpened: false,
      deleteSectionConfirmationDialogOpened: false,
      templateCustomizationDialogOpened: false,
      exportTemplateDialogOpened: false,
      sectionsToInclude: [],
      selectedTemplateName: '',
      savingTemplateEdition: false,
      selectedTemplateDocumentPath: '',
      selectedActionButtonText: '',
      isAnnual: false,
      isOnboarding: false,
      templateSettingsDialogOpened: false,
      editEngagementLetter: false,
      editForeignPreparerDisclosure: false,
      loading: true,
      templateLoading: false,
      firmGlossary: null,
      searchQuery: null,
      isSendPreviewToMeDialogOpened: false,
      isSendingPreviewToMe: false,
      questionTypes: [
        {
          text: 'Yes/No',
          value: 'boolean',
        },
        {
          text: 'Multiple Choice',
          value: 'select',
        },
        {
          text: 'Multiple Select',
          value: 'multiple',
        },
        {
          text: 'Upload',
          value: 'upload',
        },
        {
          text: 'Date',
          value: 'date',
        },
        {
          text: 'Short Answer',
          value: 'text',
        },
        {
          text: 'Long Answer',
          value: 'textarea',
        },
        {
          text: 'Client Entries',
          value: 'entries',
        },
        {
          text: 'Sensitive Information',
          value: 'password',
        },
        {
          text: 'Email',
          value: 'email',
        },
        {
          text: 'Phone',
          value: 'phone',
        },
      ],
      firstFollowUpQuestionTypes: [
        {
          text: 'Yes/No',
          value: 'boolean',
        },
        {
          text: 'Multiple Choice',
          value: 'select',
        },
        {
          text: 'Multiple Select',
          value: 'multiple',
        },
        {
          text: 'Upload',
          value: 'upload',
        },
        {
          text: 'Date',
          value: 'date',
        },
        {
          text: 'Short Answer',
          value: 'text',
        },
        {
          text: 'Long Answer',
          value: 'textarea',
        },
        {
          text: 'Sensitive Information',
          value: 'password',
        },
        {
          text: 'Client Entries',
          value: 'entries',
        },
        {
          text: 'Email',
          value: 'email',
        },
        {
          text: 'Phone',
          value: 'phone',
        },
      ],
      secondFollowUpQuestionTypes: [
        {
          text: 'Yes/No',
          value: 'boolean',
        },
        {
          text: 'Multiple Choice',
          value: 'select',
        },
        {
          text: 'Multiple Select',
          value: 'multiple',
        },
        {
          text: 'Upload',
          value: 'upload',
        },
        {
          text: 'Date',
          value: 'date',
        },
        {
          text: 'Short Answer',
          value: 'text',
        },
        {
          text: 'Long Answer',
          value: 'textarea',
        },
        {
          text: 'Sensitive Information',
          value: 'password',
        },
        {
          text: 'Email',
          value: 'email',
        },
        {
          text: 'Phone',
          value: 'phone',
        },
      ],
      entryInputTypes: [
        {
          text: 'Yes/No',
          value: 'boolean',
        },
        {
          text: 'Multiple Choice',
          value: 'select',
        },
        {
          text: 'Multiple Select',
          value: 'multiple',
        },
        {
          text: 'Upload',
          value: 'upload',
        },
        {
          text: 'Date',
          value: 'date',
        },
        {
          text: 'Short Answer',
          value: 'text',
        },
        {
          text: 'Long Answer',
          value: 'textarea',
        },
        {
          text: 'Sensitive Information',
          value: 'password',
        },
        {
          text: 'Email',
          value: 'email',
        },
        {
          text: 'Phone Number',
          value: 'phone',
        },
      ],
      selectedQuestion: {
        id: null,
        text: '',
        question_type: 'boolean',
        section: 'Other Information',
        number_of_characters: null,
        disable_answer_later: false,
        disable_not_applicable: false,
        stack_prior_year: false,
        optional: false,
        options: [],
        mapped_form: null,
        mapped_key: null,
      },
      defaultQuestion: {
        id: null,
        text: '',
        question_type: 'boolean',
        section: 'Other Information',
        number_of_characters: null,
        disable_answer_later: false,
        disable_not_applicable: false,
        stack_prior_year: false,
        optional: false,
        options: [],
        mapped_form: null,
        mapped_key: null,
      },
      saveForm: null,
      newQuestionnaireTemplate: {
        name: '',
        engagement_letter: DEFAULT_ENGAGEMENT_LETTER,
        is_annual: true,
        questionnaire_questions: [],
        sections: [],
      },
      newSectionName: '',
      selectedSectionType: null,
      newSectionInstruction: '',
      addSectionDialogOpened: '',
      relatedElement: null,
      draggedElement: null,
      selectedSection: '',
      newFollowUpQuestions: [],
      templateDeleting: false,
      selectedQuestionnaire: null,
      templateLinkDialogOpened: false,
      editSectionDialogOpened: false,
      attachDocumentToQuestionDialogOpened: false,
      documentImporting: false,
      documentToImport: null,
      questionSaving: false,
      isDraggingFollowUpQuestion: false,
      currentUserFirm: null,
      selectedFormat: null,
      mapToTaxSoftware: false,
      exportableForms: [],
      orderedSections: [],
      conditionalSectionOn: false,
      sectionToShow: null,
      selectedTriggerAnswer: null,
      stripeConnected: false,
      selectedDepositAmount: null,
    };
  },
  computed: {
    filteredTemplates() {
      if (!this.searchQuery) return this.questionnaireTemplates;
      // Filter templates based on the search query
      return this.questionnaireTemplates.filter(template =>
        template.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
      );
    },

    editorEditOptions() {
      return getEditorOptions('Edit Question');
    },
    editorNewOptions() {
      return getEditorOptions('New Question');
    },
    editorAgreementSignatureOptions() {
      return getEditorOptions('Type in agreement');
    },
    editorCreateOptions() {
      return getEditorOptions('Write a Question');
    },
    ...mapGetters({
      currentUser: 'user',
    }),
    templateNameRules() {
      return [
        v => !!v || 'Template name is required',
        v => v.indexOf('/') === -1 || 'Slash ( / ) character is not allowed',
        v =>
          this.selectedTemplateNameIsUnique || 'Template name must be unique',
      ];
    },
    canCreateTemplate() {
      return (
        this.newQuestionnaireTemplate &&
        (isEmpty(this.newQuestionnaireTemplate.name) ||
          this.newQuestionnaireTemplate.name.indexOf('/') > -1)
      );
    },
    orderedQuestionnaireSections() {
      return this.orderedSections.filter(
        s => this.sectionKey(s) === 'questionnaire',
      );
    },
    selectedTemplateNameIsUnique() {
      return !this.questionnaireTemplates
        .filter(t => t.id !== this.selectedQuestionnaire.id)
        .map(t => t.name)
        .includes(this.selectedTemplateName);
    },
    isTemplateFormNotValid() {
      return (
        isEmpty(this.selectedTemplateName) ||
        !this.selectedTemplateNameIsUnique ||
        this.selectedTemplateName.indexOf('/') > -1
      );
    },
    dragOptions() {
      return {
        animation: 0,
        group: 'description',
        ghostClass: 'ghost',
        handle: '.drag_handle',
        filter:
          'trix-editor, .trix-editor, .ql-message-input, .trix-toolbar, .trix-content',
        preventOnFilter: false,
      };
    },
    newSectionSavable() {
      return (
        !isEmpty(this.newSectionName) &&
        this.selectedSectionType !== null &&
        !this.newSectionName.includes('/') &&
        !this.newSectionName.includes('"')
      );
    },
    sectionSavable() {
      return (
        !isEmpty(this.selectedSection.name) &&
        this.selectedSection.section_type !== null &&
        !this.selectedSection.name.includes('/') &&
        !this.selectedSection.name.includes('"') &&
        (!this.conditionalSectionOn ||
          (this.sectionToShow !== null &&
            this.selectedQuestion &&
            this.selectedSection.trigger_answer))
      );
    },
    showPriorYearStackable() {
      return (
        this.currentUserFirm &&
        this.currentUserFirm.preferences &&
        this.currentUserFirm.preferences.prior_year_document_handling ===
          'within_sections'
      );
    },
    showTaxSoftwareMapping() {
      return (
        this.currentUserFirm &&
        this.currentUserFirm.preferences &&
        this.currentUserFirm.preferences.export_to_tax_software_enabled
      );
    },
    filteredSections() {
      return this.sectionTypes.filter(s => !s.hidden);
    },
  },
  methods: {
    isEmpty,
    moment,
    cloneDeep,
    union,
    intersection,
    async checkStripeIntegration() {
      try {
        const response = await API.integration.checkStripeIntegration();

        this.stripeConnected = response.data.connected;
      } catch (error) {}
    },
    showRequiredOptionCountAlert(question) {
      return (
        (question.options.length < 2 &&
          (question.question_type === 'select' ||
            question.question_type === 'multiple')) ||
        (question.options.length < 1 && question.question_type === 'upload')
      );
    },
    selectFilteredQuestionnaire(questionnaireId) {
      const filteredQuestionnaire = this.filteredTemplates.find(
        fq => fq.id === questionnaireId,
      );
      const findRealIndex =
        filteredQuestionnaire !== -1
          ? this.questionnaireTemplates.findIndex(
              qt => qt.id === filteredQuestionnaire.id,
            )
          : 0;

      this.active = findRealIndex + 1;
    },
    saveSectionTriggerAnswer() {
      this.selectedSection.trigger_answer = this.selectedTriggerAnswer.join(
        '%!%',
      );
    },
    changeOptions(question, oldOption, newOption) {
      this.getFollowUpQuestions(question).forEach(followUpQuestion => {
        if (followUpQuestion.trigger_for_follow_up_question === oldOption) {
          followUpQuestion.trigger_for_follow_up_question = newOption;
        }
      });
    },
    createStateDropdown(question, abbreviated) {
      return Object.keys(statesRecord).map(state => {
        const option = abbreviated ? state : statesRecord[state];

        question.options.push(option);
      });
    },
    createCountryDropdown(question) {
      return Object.keys(countriesRecord).map(country => {
        question.options.push(country);
      });
    },
    createUSTreasuryCurrencyDropdown(question) {
      return usTreasuryForeignCurrenciesRecord.map(currency => {
        question.options.push(currency);
      });
    },
    createCurrencyDropdown(question, withCodes = false) {
      if (withCodes) {
        return Object.keys(currenciesRecord).map(currency => {
          const option = `${currency} (${currenciesRecord[currency]})`;
          question.options.push(option);
        });
      } else {
        return Object.keys(currenciesRecord).map(currency => {
          question.options.push(currency);
        });
      }
    },
    useDefaultSectionInstruction(_section, _instruction) {
      if (this.addSectionDialogOpened) {
        this.newSectionInstruction = this.matchSection(
          this.selectedSectionType,
        ).defaultInstruction;
      } else {
        this.selectedSection.instruction = this.matchSection(
          this.selectedSection.section_type,
        ).defaultInstruction;
      }
    },
    matchSection(sectionType) {
      const section = this.sectionTypes.find(t => t.id === sectionType);
      if (section) {
        return section;
      } else {
        return this.sectionTypes.find(t => t.key === 'custom');
      }
    },
    isProtectedClientEntry(question) {
      const activeSection = this.orderedQuestionnaireSections.find(
        section => section.id === question.section_id,
      );
      const isEntriesType = question.question_type === 'entries';
      const hasSorabanEmail = this.currentUser.email
        .toLowerCase()
        .includes('@soraban.com');

      if (!isEntriesType || hasSorabanEmail || !activeSection) return false;

      return this.customSections
        .map(s => s.name)
        .concat(this.customSections.map(s => s.otherNames).flat())
        .includes(activeSection.name);
    },
    resetSelectedQuestion() {
      this.selectedQuestion.number_of_characters = null;
      if (!this.canBeFollowedUp(this.selectedQuestion)) {
        this.newFollowUpQuestions = [];
      }
    },
    openDuplicateQuestionDialog(question) {
      this.selectedQuestion = question;
      this.selectTemplateDialogOpened = true;
    },
    async getCurrentUserFirm() {
      const response = await API.firm.getCurrentUserFirm();
      const isFirmGlossaryEnabled = get(
        response,
        'data.preferences.firm_glossary_enabled',
        false,
      );

      this.currentUserFirm = response.data;

      if (isFirmGlossaryEnabled) {
        const glossaryResponse = await API.firm.getFirmVocabs(response.data.id);
        this.firmGlossary = glossaryResponse.data;
      }
    },
    findExportableFormForSelectedForm(formName) {
      return this.exportableForms.find(f => f.name === formName);
    },
    async getExportableForms() {
      try {
        const response = await API.extractedDocument.getExportableForms();

        this.exportableForms = response.data;
      } catch (error) {
        console.error(error);
      }
    },
    openTemplateSettingsDialog() {
      this.selectedTemplateName = this.selectedQuestionnaire.name;
      this.selectedTemplateDocumentPath = this.selectedQuestionnaire.document_path;
      this.isAnnual = this.selectedQuestionnaire.is_annual;
      this.selectedDepositAmount = this.selectedQuestionnaire.deposit_amount;
      this.selectedActionButtonText =
        this.selectedQuestionnaire.action_button_text || '';
      this.templateSettingsDialogOpened = true;
    },
    truncateString(str, num) {
      if (!str || str.length <= num) {
        return str;
      }
      return str.slice(0, num) + '...';
    },
    async addFile(e) {
      this.documentImporting = true;
      let droppedFiles = e.dataTransfer.files[0];

      if (!droppedFiles) return;

      this.documentToImport = droppedFiles;
      this.documentImporting = false;
    },
    removeFile(file) {
      this.files = this.files.filter(f => {
        return f !== file;
      });
    },
    pickDocument() {
      const input = this.$refs.selectedDocument;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocument.click();
    },
    async onDocumentPicked(e) {
      this.documentImporting = true;
      const document = e.target.files[0];

      if (document) {
        this.documentToImport = document;
        this.documentImporting = false;
      }
    },
    async attachDocumentToQuestion() {
      this.documentImporting = true;

      try {
        const {
          data: { id, upload_link },
        } = await API.questionnaireTemplate.getTemporaryUploadLinkForQuestionnaireTemplate();

        await API.integration.uploadToDropbox(
          upload_link,
          this.documentToImport,
        );

        const response = await API.questionnaireTemplate.attachDocumentToQuestion(
          {
            id,
            documentName: this.documentToImport.name,
            questionId: this.selectedQuestion.id,
          },
        );

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: `There was a problem uploading the document.`,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Successfully uploaded.',
          });
          this.selectedQuestion.document_link = response.data.document_link;
          let oldQuestion = this.selectedQuestionnaire.questionnaire_questions.find(
            q => q.id === this.selectedQuestion.id,
          );
          Object.assign(oldQuestion, this.selectedQuestion);
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the file',
        });
      } finally {
        this.attachDocumentToQuestionDialogOpened = false;
        this.documentImporting = false;
        this.documentToImport = null;
      }
    },
    deleteAttachedDocument() {
      API.questionnaireTemplate.deleteAttachedDocument({
        id: this.selectedQuestionnaire.id,
        questionId: this.selectedQuestion.id,
      });
      this.selectedQuestion.document_link = null;
      this.$store.dispatch('application/setSuccessNotification', {
        message: 'Successfully deleted.',
      });
      let oldQuestion = this.selectedQuestionnaire.questionnaire_questions.find(
        q => q.id === this.selectedQuestion.id,
      );
      Object.assign(oldQuestion, this.selectedQuestion);
    },
    openDeleteSectionDialog(section) {
      this.selectedSection = section;
      this.deleteSectionConfirmationDialogOpened = true;
    },
    async openEditSectionDialog(section) {
      this.selectedSection = Object.assign({}, section);

      if (section.trigger_question_id) {
        let question = this.selectedQuestionnaire.questionnaire_questions.find(
          q => q.id === section.trigger_question_id,
        );

        if (question) {
          this.sectionToShow = this.selectedQuestionnaire.sections.find(
            s => s.id === question.section_id,
          );
          this.selectedQuestion = await this.allSectionQuestionsForTrigger(
            this.sectionToShow,
          ).find(q => q.id === section.trigger_question_id);
          if (this.selectedSection.trigger_answer) {
            this.selectedTriggerAnswer = section.trigger_answer.split('%!%');
          } else {
            this.selectedTriggerAnswer = null;
          }
          this.conditionalSectionOn = true;
          this.editSectionDialogOpened = true;
          return;
        }
      }

      this.conditionalSectionOn = false;
      this.sectionToShow = null;
      this.selectedQuestion = null;
      section.trigger_question_id = null;
      section.trigger_answer = null;
      this.editSectionDialogOpened = true;
    },
    closeSectionDialog() {
      this.conditionalSectionOn = false;
      this.editSectionDialogOpened = false;
      this.selectedTriggerAnswer = null;
    },
    async deleteSection() {
      try {
        await API.questionnaireTemplate.deleteSection({
          questionnaireTemplateId: this.selectedQuestionnaire.id,
          sectionId: this.selectedSection.id,
        });

        this.getQuestionnaireTemplate(this.selectedQuestionnaire.id);
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You have succesfully removed the ${
            this.projectViewEnabled ? 'section' : 'step'
          }.`,
        });
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem.',
        });
        console.error(error);
      } finally {
        this.deleteSectionConfirmationDialogOpened = false;
      }
    },
    async saveSection(section) {
      if (this.conditionalSectionOn) {
        section.trigger_question_id = this.selectedQuestion.id;
      } else {
        section.trigger_question_id = null;
      }

      try {
        await API.questionnaireTemplate.saveSection(section);
        await this.getQuestionnaireTemplate(this.selectedQuestionnaire.id);

        this.$store.dispatch('application/setSuccessNotification', {
          message: `You have succesfully saved the ${
            this.projectViewEnabled ? 'section' : 'step'
          }.`,
        });
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem saving section.',
        });
        console.error(error);
      } finally {
        this.closeSectionDialog();
      }
    },
    openAddSectionDialog() {
      this.addSectionDialogOpened = true;
      this.newSectionName = '';
      this.newSectionInstruction = '';
      this.selectedSectionType = null;
    },
    async addSection() {
      try {
        await API.questionnaireTemplate.addSection({
          sectionName: this.newSectionName,
          sectionType: this.selectedSectionType,
          instruction: this.newSectionInstruction,
          questionnaireTemplateId: this.selectedQuestionnaire.id,
        });

        this.getQuestionnaireTemplate(this.selectedQuestionnaire.id);
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You have succesfully added the ${
            this.projectViewEnabled ? 'section' : 'step'
          }.`,
        });
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem.',
        });
        console.error(error);
      } finally {
        this.addSectionDialogOpened = false;
      }
    },
    questionSavable(question) {
      if (this.optionsEditing) {
        return false;
      }

      if (question.question_type === 'entries') {
        if (
          this.getFollowUpQuestions(question, true).every(q =>
            this.entryInputSavable(q),
          )
        ) {
          if (
            question.question_type === 'select' ||
            question.question_type === 'multiple'
          ) {
            return (
              question.text &&
              question.section_id &&
              question.options.length > 1
            );
          } else if (question.question_type === 'upload') {
            return question.text && question.section;
          } else {
            return (
              question.text && question.section_id && question.question_type
            );
          }
        } else {
          return false;
        }
      } else {
        if (
          this.getFollowUpQuestions(question, true).every(q =>
            this.followUpQuestionSavable(q),
          )
        ) {
          if (
            question.question_type === 'select' ||
            question.question_type === 'multiple'
          ) {
            return (
              question.text &&
              question.section_id &&
              question.options.length > 1
            );
          } else if (question.question_type === 'upload') {
            return question.text && question.section_id;
          } else {
            return (
              question.text && question.section_id && question.question_type
            );
          }
        } else {
          return false;
        }
      }
    },
    entryInputSavable(question) {
      if (
        question.question_type === 'select' ||
        question.question_type === 'multiple'
      ) {
        return question.text && question.options.length > 1;
      } else {
        return question.text && question.question_type;
      }
    },
    canBeFollowedUp(question) {
      return (
        question.question_type === 'select' ||
        question.question_type === 'boolean' ||
        question.question_type === 'multiple'
      );
    },
    followUpQuestionSavable(question) {
      const followupQuestions = this.getFollowUpQuestions(question, true);
      const isMultiQuestion =
        question.question_type === 'select' ||
        question.question_type === 'multiple';

      if (followupQuestions.every(q => this.entryInputSavable(q))) {
        if (isMultiQuestion) {
          return Boolean(
            question.text && question.section_id && question.options.length > 1,
          );
        } else if (question.question_type === 'upload') {
          return Boolean(question.text && question.section_id);
        } else {
          return Boolean(
            question.text && question.section_id && question.question_type,
          );
        }
      } else if (
        !isEmpty(followupQuestions) &&
        !this.createQuestionDialogOpened &&
        !this.newFollowUpQuestions.includes(question)
      ) {
        return followupQuestions.every(q => this.followUpQuestionSavable(q));
      } else {
        if (isMultiQuestion) {
          return Boolean(
            question.text &&
              question.trigger_for_follow_up_question &&
              question.options.length > 1,
          );
        } else {
          return Boolean(
            question.text && question.trigger_for_follow_up_question,
          );
        }
      }
    },
    async exportTemplate() {
      let sections = [];

      try {
        this.sectionsToInclude.forEach((sectionInclude, i) => {
          if (sectionInclude) {
            sections.push(this.selectedQuestionnaire.sections[i]);
          }
        });
        this.exportTemplateDialogOpened = false;
        const response = await API.questionnaireTemplate.exportTemplate({
          questionnaireTemplateId: this.selectedQuestionnaire.id,
          format: this.selectedFormat,
          sections,
        });

        if (this.selectedFormat === 'Spreadsheet') {
          this.saveAs(response.data, `${this.selectedQuestionnaire.name}.xls`);
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message:
              "Export queued! You will get a notification once it's ready to be downloaded.",
          });
        }
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem assigning a template.',
        });

        console.error(error);
      } finally {
        this.selectedFormat = null;
      }
    },
    saveAs(uri, filename) {
      let link = document.createElement('a');
      if (typeof link.download === 'string') {
        link.href = uri;
        link.download = filename;
        // Firefox requires the link to be in the body
        document.body.appendChild(link);
        // simulate click
        link.click();
        // remove the link when done
        document.body.removeChild(link);
      } else {
        window.open(uri, '_blank');
      }
    },
    toLetters(num) {
      let mod = num % 26;
      let pow = (num / 26) | 0;
      let out = mod ? String.fromCharCode(64 + mod) : (--pow, 'Z');

      return pow ? this.toLetters(pow) + out : out;
    },
    async deleteFollowUpQuestion(question) {
      if (question.id) {
        await this.deleteQuestion(question.id);
      } else {
        const index = this.selectedQuestionnaire.questionnaire_questions.findIndex(
          q =>
            q.text === question.text &&
            q.follow_up_question_id === question.follow_up_question_id,
        );
        if (index >= 0) {
          this.selectedQuestionnaire.questionnaire_questions.splice(index, 1);
        } else {
          const index2 = this.newFollowUpQuestions.findIndex(
            q => q.text === question.text,
          );
          this.newFollowUpQuestions.splice(index2, 1);
        }
      }
    },
    closeQuestionDialogsAndResetQuestionnaireQuestions() {
      this.selectedQuestionnaire.questionnaire_questions = this.selectedQuestionnaire.questionnaire_questions.filter(
        q => q.id,
      );
      this.editQuestionDialogOpened = false;
      this.createQuestionDialogOpened = false;
      this.showRequiredFieldsError = false;
    },
    // Generates a clean new question
    generateQuestion(
      questionType,
      mappedForm = null,
      presetTrigger = null,
      sectionId = null,
      followUpQuestionId = null,
      options = [],
      text = '',
    ) {
      const question = {
        disable_answer_later: false,
        disable_not_applicable: false,
        entryOptions: [],
        follow_up_question_id: followUpQuestionId,
        mapped_form: mappedForm,
        mapped_key: null,
        number_of_characters: null,
        optional: false,
        options: options,
        question_type: questionType,
        section_id: sectionId,
        stack_prior_year: false,
        text: text,
        trigger_for_follow_up_question: presetTrigger,
      };

      return question;
    },
    // Generates a clean new followup question based on question entry point
    generateFollowupQuestion(question, fId) {
      const followUpQuestionId = fId || question.id;
      const isEntriesType = question.question_type === 'entries';

      // Get the preset trigger from question
      const getPresetTrigger = () => {
        if (isEntriesType) return 'Template';

        const isSelectOrMultipleType = Boolean(
          question.question_type === 'select' ||
            question.question_type === 'multiple',
        );
        const numberOfExistingFollowUpQuestions = this.getFollowUpQuestions(
          question,
        ).length;
        const presetTrigger = isSelectOrMultipleType
          ? question.options[numberOfExistingFollowUpQuestions]
          : numberOfExistingFollowUpQuestions === 0
          ? 'Yes'
          : 'No';

        return presetTrigger;
      };

      const questionType = isEntriesType ? 'text' : 'boolean';
      const mappedForm = isEntriesType ? undefined : this.savedForm;
      const presetTrigger = getPresetTrigger();

      const genericQuestion = this.generateQuestion(
        questionType,
        mappedForm,
        presetTrigger,
        question.section_id,
        followUpQuestionId,
      );
      const followupQuestion = {
        ...genericQuestion,
        entryOptions: [], // NOTE @chris This is an Array<{type: EntryType, text: string, options: text[]}> and should only extend a future "QuestionType" locally
      };

      return followupQuestion;
    },
    async addFollowUpQuestion(question) {
      const potentialQuestions = this.selectedQuestionnaire.questionnaire_questions.filter(
        q =>
          q.text === question.text &&
          q.trigger_for_follow_up_question ===
            question.trigger_for_follow_up_question &&
          q.follow_up_question_id === question.follow_up_question_id,
      );

      if (
        potentialQuestions.length > 1 &&
        potentialQuestions.filter(q => !q.id).length > 0
      ) {
        // We want this early to return to avoid any data being lost. It should rarely come up but if it does, we really don't want our users to lose any data.
        return this.$store.dispatch('application/setErrorNotification', {
          message:
            'You are adding a follow-up question, for the same question with the same trigger, more than once. If you wish to proceed, please click "Save" first.',
        });
      }

      const isExistingQuestion = Boolean(question.id);
      const basicFollowupQuestion = this.generateFollowupQuestion(question);

      // Create a followup question for an existing question
      if (isExistingQuestion)
        return this.selectedQuestionnaire.questionnaire_questions.push(
          basicFollowupQuestion,
        );

      // Create a followup question for any non existing question
      if (!question.follow_up_question_id)
        return this.newFollowUpQuestions.push(basicFollowupQuestion);

      // Create followup questions for 2nd level questions
      try {
        const { data } = await API.questionnaireTemplate.createQuestion({
          followUpQuestionId: question.follow_up_question_id,
          mapped_form: question.mapped_form,
          mapped_key: question.mapped_key,
          options: question.options,
          questionnaireTemplateId: this.selectedQuestionnaire.id,
          questionType: question.question_type,
          section_id: question.section_id,
          text: question.text,
          triggerForFollowUpQuestion: question.trigger_for_follow_up_question,
        });

        const index = this.selectedQuestionnaire.questionnaire_questions.findIndex(
          q =>
            q.text === question.text &&
            q.trigger_for_follow_up_question ===
              question.trigger_for_follow_up_question &&
            q.follow_up_question_id === question.follow_up_question_id,
        );

        this.selectedQuestionnaire.questionnaire_questions.splice(
          index,
          1,
          data,
        );

        // Create a new one (future change: this should come from data response)
        const followupQuestion = this.generateFollowupQuestion(
          question,
          data.id,
        );

        return this.selectedQuestionnaire.questionnaire_questions.push(
          followupQuestion,
        );
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem.',
        });
        console.error(error);
      }
    },
    sortedQuestions(section) {
      return this.selectedQuestionnaire.questionnaire_questions
        .filter(q => q.section_id === section.id && !q.follow_up_question_id)
        .sort((a, b) => a.order - b.order);
    },
    allSectionQuestionsForTrigger(section) {
      return this.selectedQuestionnaire.questionnaire_questions
        .filter(
          q =>
            q.section_id === section.id &&
            ['boolean', 'select', 'multiple'].includes(q.question_type),
        )
        .sort((a, b) => a.order && a.order - b.order)
        .map(q => {
          return {
            formattedQuestionText: q.text.replace(/(<([^>]+)>)/gi, ''),
            ...q,
          };
        });
    },
    addOption(question, option) {
      if (!option || option.trim() === '') {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'You cannot have blank option',
        });
      } else if (
        question.options
          .map(o => o.toLowerCase())
          .includes(option.toLowerCase())
      ) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Duplicate option',
        });
      } else {
        question.options.push(option.trim());
      }
    },
    addEntryItem(question) {
      const newEntryOption = {
        type: 'text',
        text: '',
        options: [],
      };

      if (!question.entryOptions) question.entryOptions = [];

      return question.entryOptions.push(newEntryOption);
    },
    addEntryOption(entryOption, event) {
      if (!entryOption.options) entryOption.options = [];

      return entryOption.options.push(event);
    },
    deleteEntryOption(question, entryOption) {
      const newEntryOptions = question.entryOptions.filter(
        eo => eo === entryOption,
      );

      question.entryOptions = newEntryOptions;
    },
    sortAndFlattenFollowUpQuestionsHashByOptions(hash, parentQuestion) {
      const options = parentQuestion && parentQuestion.options;

      if (!options || options.length === 0) {
        return Object.keys(hash)
          .map(key => hash[key])
          .flatten();
      }

      return Object.keys(hash)
        .sort((a, b) => {
          const aTrigger = a;
          const bTrigger = b;

          // If aTrigger is null, it goes to the bottom
          if (aTrigger === null) return 1;
          // If bTrigger is null, it goes to the bottom
          if (bTrigger === null) return -1;

          const aIndex = options.indexOf(aTrigger);
          const bIndex = options.indexOf(bTrigger);

          // If both have valid indices, sort by the index in options
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }

          // If one of them doesn't exist in options, keep it in the original order
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;

          return 0;
        })
        .map(key => hash[key])
        .flatten();
    },
    sortQuestions(questions, parentQuestion) {
      let hash = {};

      questions.forEach(q => {
        if (hash[q.trigger_for_follow_up_question]) {
          hash[q.trigger_for_follow_up_question].push(q);
        } else {
          hash[q.trigger_for_follow_up_question] = [q];
        }
      });

      const sortByTrigger = questions => {
        return questions.sort((a, b) => {
          if (typeof a.order === 'number' && typeof b.order === 'number') {
            // initially order by the trigger (if they're not already in order by that);
            if (
              a.trigger_for_follow_up_question &&
              b.trigger_for_follow_up_question &&
              a.trigger_for_follow_up_question.localeCompare(
                b.trigger_for_follow_up_question,
              ) !== 0
            ) {
              return a.trigger_for_follow_up_question.localeCompare(
                b.trigger_for_follow_up_question,
              );
            }

            return a.order - b.order;
          }

          return a.id - b.id;
        });
      };

      Object.keys(hash).forEach(key => {
        hash[key] = sortByTrigger(hash[key]);
      });

      let allQuestions = this.sortAndFlattenFollowUpQuestionsHashByOptions(
        hash,
        parentQuestion,
      );

      if (
        allQuestions.some(q => typeof q.order === 'number') &&
        allQuestions.some(q => typeof q.order !== 'number')
      ) {
        const withoutOrder = allQuestions.filter(
          q => typeof q.order !== 'number',
        );
        const withOrder = allQuestions.filter(q => typeof q.order === 'number');
        allQuestions = [...withOrder, ...withoutOrder];
      }

      return allQuestions;
    },
    getFollowUpQuestions(question, fromQuestionSavable = false) {
      if (question.id) {
        if (fromQuestionSavable) {
          // There was a bug that I couldn't quite track down but I believe this is the root cause. And if we're calling this from questionSavable, we don't need the questions properly sorted. We're just checking each of the follow ups to see if their savable. So I'll use the old method.
          // https://linear.app/soraban/issue/SOR-11053/fix-bug-unable-to-save-edited-question-in-mga
          return this.selectedQuestionnaire.questionnaire_questions.filter(
            q => q.follow_up_question_id === question.id,
          );
        }

        return this.sortQuestions(
          this.selectedQuestionnaire.questionnaire_questions.filter(
            q => q.follow_up_question_id === question.id,
          ),
          question,
        );
      } else {
        return this.newFollowUpQuestions;
      }
    },
    getFollowUpQuestionsWithTrigger(question, trigger) {
      return this.sortQuestions(
        this.selectedQuestionnaire.questionnaire_questions.filter(
          q =>
            q.follow_up_question_id === question.id &&
            q.trigger_for_follow_up_question === trigger,
        ),
        question,
      );
    },
    getTriggers(questions) {
      return questions
        .map(q => q.trigger_for_follow_up_question)
        .filter(this.onlyUnique);
    },
    onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    },
    onMove({ relatedContext, draggedContext }) {
      this.relatedElement = relatedContext.element;
      this.draggedElement = draggedContext.element;
    },
    questionMoveEnd() {
      if (this.draggedElement && this.relatedElement) {
        if (this.draggedElement.section_id === this.relatedElement.section_id) {
          const previousOrder = this.draggedElement.order;
          this.draggedElement.order = this.relatedElement.order;
          API.questionnaireTemplate.updateQuestion({
            set_id: this.selectedQuestionnaire.id,
            id: this.draggedElement.id,
            order: this.draggedElement.order,
          });

          if (previousOrder > this.draggedElement.order) {
            this.selectedQuestionnaire.questionnaire_questions
              .filter(
                q =>
                  q.section_id === this.draggedElement.section_id &&
                  q.order < previousOrder &&
                  q.order >= this.draggedElement.order &&
                  q.id !== this.draggedElement.id,
              )
              .forEach(q => {
                q.order += 1;
                API.questionnaireTemplate.updateQuestion({
                  set_id: this.selectedQuestionnaire.id,
                  id: q.id,
                  order: q.order,
                });
              });
          } else if (previousOrder < this.draggedElement.order) {
            this.selectedQuestionnaire.questionnaire_questions
              .filter(
                q =>
                  q.section_id === this.draggedElement.section_id &&
                  q.order > previousOrder &&
                  q.order <= this.draggedElement.order &&
                  q.id !== this.draggedElement.id,
              )
              .forEach(q => {
                q.order -= 1;
                API.questionnaireTemplate.updateQuestion({
                  set_id: this.selectedQuestionnaire.id,
                  id: q.id,
                  order: q.order,
                });
              });
          }
        }

        this.relatedElement = null;
        this.draggedElement = null;
      }
    },
    async giveAllFollowUpQuestionsNewOrder(
      parentQuestion,
      draggedQuestion,
      relatedQuestion,
      indexOfDraggedElementOverride,
      indexOfRelatedElementOverride,
    ) {
      let followUpQuestions = this.getFollowUpQuestions(parentQuestion);

      if (relatedQuestion && draggedQuestion) {
        const indexOfDraggedElement =
          indexOfDraggedElementOverride ||
          followUpQuestions.findIndex(q => q.id === draggedQuestion.id);
        const indexOfRelatedElement =
          indexOfRelatedElementOverride ||
          followUpQuestions.findIndex(q => q.id === relatedQuestion.id);

        if (
          relatedQuestion.trigger_for_follow_up_question ===
          draggedQuestion.trigger_for_follow_up_question
        ) {
          followUpQuestions[indexOfDraggedElement] = relatedQuestion;
          followUpQuestions[indexOfRelatedElement] = draggedQuestion;
        } else {
          // Remove draggedQuestion from its original position
          followUpQuestions.splice(indexOfDraggedElement, 1);
          // Insert draggedQuestion at indexOfRelatedElement
          followUpQuestions.splice(indexOfRelatedElement, 0, draggedQuestion);
          // After insertion, adjust indexOfRelatedElement to point to the next position
          followUpQuestions.splice(
            indexOfRelatedElement + 1,
            0,
            relatedQuestion,
          );
        }
      }

      let hash = {};

      followUpQuestions.forEach(q => {
        if (hash[q.trigger_for_follow_up_question]) {
          hash[q.trigger_for_follow_up_question].push(q);
        } else {
          hash[q.trigger_for_follow_up_question] = [q];
        }
      });

      followUpQuestions = this.sortAndFlattenFollowUpQuestionsHashByOptions(
        hash,
        parentQuestion,
      );

      followUpQuestions.map((question, index) => {
        this.selectedQuestionnaire.questionnaire_questions.find(
          q => question.id === q.id,
        ).order = index + 1;

        return API.questionnaireTemplate.updateQuestion({
          set_id: this.selectedQuestionnaire.id,
          id: question.id,
          order: index + 1,
        });
      });

      await (async () => {
        const updatePromises = followUpQuestions.map(
          async (question, index) => {
            this.selectedQuestionnaire.questionnaire_questions.find(
              q => question.id === q.id,
            ).order = index + 1;

            await API.questionnaireTemplate.updateQuestion({
              set_id: this.selectedQuestionnaire.id,
              id: question.id,
              order: index + 1,
            });
          },
        );

        await Promise.all(updatePromises);
      })();
    },
    async followUpQuestionMoveEnd(parentQuestion) {
      try {
        this.isDragging = false;

        if (!this.draggedElement.text) {
          return this.$store.dispatch('application/setErrorNotification', {
            message:
              'Each question must have a value before you can drag and drop them.',
          });
        }

        if (this.draggedElement && this.relatedElement) {
          if (
            this.draggedElement.follow_up_question_id ===
            this.relatedElement.follow_up_question_id
          ) {
            this.isDraggingFollowUpQuestion = true;
            const previousOrder = this.draggedElement.order;
            const nextOrder = this.relatedElement.order;
            this.draggedElement.order = this.relatedElement.order;

            const bothHaveFollowUps =
              this.draggedElement &&
              this.draggedElement.trigger_for_follow_up_question &&
              this.relatedElement &&
              this.relatedElement.trigger_for_follow_up_question;

            if (bothHaveFollowUps) {
              const isYesNo =
                ['Yes', 'No'].includes(
                  this.draggedElement.trigger_for_follow_up_question,
                ) &&
                ['Yes', 'No'].includes(
                  this.relatedElement.trigger_for_follow_up_question,
                );
              const triggersAreDifferent =
                this.relatedElement.trigger_for_follow_up_question !==
                this.draggedElement.trigger_for_follow_up_question;

              if (triggersAreDifferent) {
                if (isYesNo) {
                  this.$store.dispatch('application/setErrorNotification', {
                    message:
                      'You cannot move a "No" follow-up question above a "Yes" follow-up question nor can you move a "Yes" follow-up question below a "No" follow-up question. If you are trying to move a new "Yes" follow-up question to the other "Yes" follow-up questions, this will be done automatically on "Save".',
                  });
                } else {
                  const trigger = this.draggedElement
                    .trigger_for_follow_up_question;

                  this.$store.dispatch('application/setErrorNotification', {
                    message: `You can only reorder your "${trigger}" follow-up questions within other "${trigger}" follow-up questions. If you wish to change the order of "${trigger}" and "${this.relatedElement.trigger_for_follow_up_question}", you will need to do so in the "Options" above. If you are adding new follow-up questions, they will be automatically saved to their respective option.`,
                  });
                }

                return;
              }
            }

            // If a new follow up question has been added and is being re-ordered before click "Save", we'll go ahead and save all follow up questions and start the re-ordering process
            if (!this.draggedElement.id || !this.relatedElement.id) {
              let followUpQuestions = this.getFollowUpQuestions(parentQuestion);
              const draggedQuestionIndex = followUpQuestions.findIndex(
                q => q.text === this.draggedElement.text,
              );
              const relatedQuestionIndex = followUpQuestions.findIndex(
                q => q.text === this.relatedElement.text,
              );

              await (async () => {
                const savePromises = this.selectedQuestionnaire.questionnaire_questions.map(
                  async question => {
                    if (!question.id) {
                      await this.saveFollowUpQuestion(question);
                    }
                  },
                );

                await Promise.all(savePromises);
              })();

              this.selectedQuestionnaire.questionnaire_questions = this.selectedQuestionnaire.questionnaire_questions.filter(
                q => q.id,
              );

              followUpQuestions = this.getFollowUpQuestions(parentQuestion);

              const draggedQuestion = followUpQuestions.find(
                q => q.text === this.draggedElement.text,
              );
              const relatedQuestion = followUpQuestions.find(
                q => q.text === this.relatedElement.text,
              );

              await this.giveAllFollowUpQuestionsNewOrder(
                parentQuestion,
                draggedQuestion,
                relatedQuestion,
                draggedQuestionIndex,
                relatedQuestionIndex,
              );

              this.isDraggingFollowUpQuestion = false;
              return;
            }

            // If re-ordering already existing follow up questions, and these follow up questions don't have an order yet (since this is a new feature), we'll update the order for all the follow up questions
            if (!previousOrder || !this.relatedElement.order) {
              await this.giveAllFollowUpQuestionsNewOrder(
                parentQuestion,
                this.draggedElement,
                this.relatedElement,
              );
              this.isDraggingFollowUpQuestion = false;
              return;
            }

            API.questionnaireTemplate.updateQuestion({
              set_id: this.selectedQuestionnaire.id,
              id: this.draggedElement.id,
              order: this.draggedElement.order,
            });

            if (previousOrder > this.draggedElement.order) {
              this.selectedQuestionnaire.questionnaire_questions
                .filter(
                  q =>
                    q.follow_up_question_id ===
                      this.draggedElement.follow_up_question_id &&
                    q.order < previousOrder &&
                    q.order >= this.draggedElement.order &&
                    q.id !== this.draggedElement.id,
                )
                .forEach(q => {
                  q.order += 1;
                  API.questionnaireTemplate.updateQuestion({
                    set_id: this.selectedQuestionnaire.id,
                    id: q.id,
                    order: q.order,
                  });
                });
            } else if (previousOrder < this.draggedElement.order) {
              this.selectedQuestionnaire.questionnaire_questions
                .filter(
                  q =>
                    q.follow_up_question_id ===
                      this.draggedElement.follow_up_question_id &&
                    q.order > previousOrder &&
                    q.order <= this.draggedElement.order &&
                    q.id !== this.draggedElement.id,
                )
                .forEach(q => {
                  q.order -= 1;
                  API.questionnaireTemplate.updateQuestion({
                    set_id: this.selectedQuestionnaire.id,
                    id: q.id,
                    order: q.order,
                  });
                });
            }
          }

          this.relatedElement = null;
          this.draggedElement = null;
        }
      } finally {
        this.isDraggingFollowUpQuestion = false;
      }
    },
    sectionMoveEnd() {
      if (this.draggedElement && this.relatedElement) {
        let order = 0;
        this.orderedSections.forEach(section => {
          API.questionnaireTemplate.saveSection({
            set_id: this.selectedQuestionnaire.id,
            id: section.id,
            order: order,
          });
          section.order = order;
          order += 1;
        });
        this.relatedElement = null;
        this.draggedElement = null;
      }
    },
    async saveTemplate() {
      const isSavable = Boolean(
        this.selectedTemplateName !== this.selectedQuestionnaire.name ||
          this.selectedTemplateDocumentPath !==
            this.selectedQuestionnaire.document_path ||
          this.isAnnual !== this.selectedQuestionnaire.is_annual ||
          this.selectedDepositAmount !==
            this.selectedQuestionnaire.deposit_amount ||
          this.selectedActionButtonText !==
            (this.selectedQuestionnaire.action_button_text || ''),
      );

      if (isSavable) {
        this.savingTemplateEdition = true;

        try {
          await API.questionnaireTemplate.updateQuestionnaireTemplate({
            id: this.selectedQuestionnaire.id,
            name: this.selectedTemplateName,
            document_path: this.selectedTemplateDocumentPath,
            is_annual: this.isAnnual,
            deposit_amount: this.selectedDepositAmount,
            action_button_text: this.selectedActionButtonText,
          });

          this.$store.dispatch('application/setSuccessNotification', {
            message: 'You have saved the template.',
          });

          this.selectedQuestionnaire.name = this.selectedTemplateName;
          this.getAllQuestionnaireTemplates();

          await this.getQuestionnaireTemplate(this.selectedQuestionnaire.id);
          this.templateSettingsDialogOpened = false;
        } catch (error) {
          console.error(error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Something went wrong saving the template.',
          });
        } finally {
          this.savingTemplateEdition = false;
        }
      } else {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There are no changes to save.',
        });
      }
    },
    openCreateQuestionDialog(section) {
      this.selectedQuestion = cloneDeep(this.defaultQuestion);
      this.selectedQuestion.mapped_form = this.savedForm;
      this.selectedQuestion.section_id = section.id;
      this.createQuestionDialogOpened = true;
    },
    openEditQuestionDialog(question) {
      this.selectedQuestion = cloneDeep(question);
      this.editQuestionDialogOpened = true;
    },
    openDeleteQuestionConfirmationDialog(question) {
      this.selectedQuestion = question;
      this.deleteQuestionConfirmationDialogOpened = true;
    },
    async sendPreviewToMe() {
      try {
        this.isSendingPreviewToMe = true;

        const {
          data: { message },
        } = await API.questionnaireTemplate.sendPreviewToMe(
          this.selectedQuestionnaire.id,
        );

        if (message) {
          this.$store.dispatch('application/setSuccessNotification', {
            message,
          });
        }
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong sending the template preview.',
        });
        console.error(error);
      } finally {
        this.isSendingPreviewToMe = false;
        this.isSendPreviewToMeDialogOpened = false;
      }
    },
    async getPreview() {
      const response = await API.questionnaireTemplate.getQuestionnaireTemplate(
        this.selectedQuestionnaire.id,
      );

      this.selectedQuestionnaire = response.data;
      this.showPreview = true;
    },
    async getReactPreview() {
      this.showReactPreview = true;
    },
    async deleteTemplate() {
      this.templateDeleting = true;

      try {
        await API.questionnaireTemplate.deleteQuestionnaireTemplate(
          this.selectedQuestionnaire.id,
        );

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'You have deleted the template.',
        });

        await this.getAllQuestionnaireTemplates();
        await this.selectMostRecentTemplate();
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem deleting the template.',
        });
        console.error(error);
      } finally {
        this.deleteTemplateConfirmationDialogOpened = false;
        this.templateDeleting = false;
      }
    },
    async deleteQuestion(id) {
      try {
        await API.questionnaireTemplate.deleteQuestion({
          id,
          setId: this.selectedQuestionnaire.id,
        });

        let index = this.selectedQuestionnaire.questionnaire_questions.findIndex(
          q => q.id === id,
        );

        this.selectedQuestionnaire.questionnaire_questions.splice(index, 1);
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'You have deleted the question.',
        });
      } catch (error) {
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem deleting the question.',
        });
      }

      this.deleteQuestionConfirmationDialogOpened = false;
    },
    async createTemplate() {
      this.newQuestionnaireTemplate.user_id = this.currentUser.id;

      try {
        await API.questionnaireTemplate.createQuestionnaireTemplate(
          this.newQuestionnaireTemplate,
        );

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'You have created a template.',
        });

        await this.getAllQuestionnaireTemplates();
        Object.assign(this.newQuestionnaireTemplate, {
          name: '',
          engagement_letter: DEFAULT_ENGAGEMENT_LETTER,
          questionnaire_questions: [],
          sections: [],
        });

        this.$nextTick(() => {
          this.active = 1;
        });
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem creating the template.',
        });
        console.error(error);
      }
    },
    async duplicateTemplate() {
      this.templateLoading = true;

      try {
        await API.questionnaireTemplate.duplicateTemplate(
          this.selectedQuestionnaire.id,
        );

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'You have duplicated a template.',
        });

        await this.getAllQuestionnaireTemplates();
        await this.selectMostRecentTemplate();
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem duplicating the template.',
        });
        console.error(error);
      } finally {
        this.templateLoading = false;
      }
    },
    async trySavingQuestion(question) {
      const isSavable = this.questionSavable(question);

      if (!isSavable) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Please fix the errors shown',
        });
        this.showRequiredFieldsError = true;
        return;
      }

      await this.saveQuestion(question);
    },
    async saveQuestion(question) {
      const isSavable = this.questionSavable(question);

      // Do nothing if question is not ready to be saved
      if (!isSavable) return;

      // Try saving the question
      try {
        const isExisting = Boolean(question.id);
        const hasFollowupQuestions = !isEmpty(
          this.getFollowUpQuestions(question),
        );

        this.questionSaving = true;

        // Updating
        if (isExisting) {
          await API.questionnaireTemplate.updateQuestion(question);

          const oldQuestion = this.selectedQuestionnaire.questionnaire_questions.find(
            q => q.id === question.id,
          );
          Object.assign(oldQuestion, question);

          // NOTE @chris We're editing questionnaire_questions in selectedQuestionnaire, but are using sections downstream
          // Keep question in sync
          const newSections = this.selectedQuestionnaire.sections.map(
            section => {
              const hasQuestions =
                section.questionnaire_questions &&
                !!section.questionnaire_questions.length;

              if (hasQuestions) {
                section.questionnaire_questions.map(q => {
                  if (q.id === question.id) q.text = question.text;

                  return q;
                });
              }

              return section;
            },
          );

          this.selectedQuestionnaire = {
            ...this.selectedQuestionnaire,
            sections: newSections,
          };

          if (hasFollowupQuestions) {
            let followUpQuestions = this.getFollowUpQuestions(question);
            const questionsWithoutOrder = followUpQuestions.filter(
              q => typeof q.order !== 'number',
            );
            const questionsWithOrder = followUpQuestions.filter(
              q => typeof q.order === 'number',
            );

            // This shouldn't happen but just in case, we'd want to set the order for the ones that don't have an order yet.
            if (
              questionsWithoutOrder.length > 0 &&
              questionsWithOrder.length > 0
            ) {
              let hash = {};

              followUpQuestions.forEach(q => {
                if (hash[q.trigger_for_follow_up_question]) {
                  hash[q.trigger_for_follow_up_question].push(q);
                } else {
                  hash[q.trigger_for_follow_up_question] = [q];
                }
              });

              followUpQuestions = this.sortAndFlattenFollowUpQuestionsHashByOptions(
                hash,
                question,
              );
            }

            await this.asyncForEach(followUpQuestions, q =>
              this.saveFollowUpQuestion(q),
            );
          }
        }

        // Create new
        else {
          const lastMaxOrder = Math.max.apply(
            Math,
            this.selectedQuestionnaire.questionnaire_questions
              .filter(
                a =>
                  a.section_id === question.section_id &&
                  typeof a.order === 'number',
              )
              .map(q => q.order),
          );
          const isLastEmpty = isEmpty(
            this.selectedQuestionnaire.questionnaire_questions.filter(
              a => a.section_id === question.section_id,
            ),
          );
          const lastOrder = isLastEmpty ? 1 : lastMaxOrder;

          const { data } = await API.questionnaireTemplate.createQuestion({
            text: question.text,
            questionType: question.question_type,
            order: lastOrder + 1,
            options: question.options,
            section_id: question.section_id,
            questionnaireTemplateId: this.selectedQuestionnaire.id,
            number_of_characters: question.number_of_characters,
            disable_answer_later: question.disable_answer_later,
            stack_prior_year: question.stack_prior_year,
            optional: question.optional,
            disable_not_applicable: question.disable_not_applicable,
            mapped_form: question.mapped_form,
            mapped_key: question.mapped_key,
          });

          this.selectedQuestion = data;
          this.selectedQuestionnaire.questionnaire_questions.push(
            this.selectedQuestion,
          );

          const hasNewFollowupQuestions = !isEmpty(this.newFollowUpQuestions);

          if (hasNewFollowupQuestions) {
            this.getFollowUpQuestions(question).forEach(q => {
              q.follow_up_question_id = this.selectedQuestion.id;
            });
            await this.asyncForEach(this.newFollowUpQuestions, q =>
              this.saveFollowUpQuestion(q),
            );
            this.newFollowUpQuestions = [];
          }
        }

        question = cloneDeep(this.defaultQuestion);
        question.mapped_form = this.savedForm;

        this.getQuestionnaireTemplate(this.selectedQuestionnaire.id);
        this.closeQuestionDialogsAndResetQuestionnaireQuestions();
        return this.$store.dispatch('application/setSuccessNotification', {
          message: 'You have saved the question.',
        });
      } catch (error) {
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem saving the question.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.questionSaving = false;
        this.editQuestionDialogOpened = false;
        this.createQuestionDialogOpened = false;
        this.showRequiredFieldsError = false;
      }
    },
    getSectionId(followUpQuestionId) {
      const parentSectionId = followUpQuestionId
        ? this.selectedQuestionnaire.questionnaire_questions.find(
            q => q.id === followUpQuestionId,
          ).section_id || null
        : null;

      return parentSectionId;
    },
    async saveFollowUpQuestion(question) {
      const isExisting = Boolean(question.id);

      // Updating the question
      if (isExisting) {
        question.section_id = this.getSectionId(question.follow_up_question_id);

        try {
          await API.questionnaireTemplate.updateQuestion(question);

          if (
            !isEmpty(this.getFollowUpQuestions(question)) &&
            !this.createQuestionDialogOpened
          ) {
            await this.asyncForEach(this.getFollowUpQuestions(question), q =>
              this.saveFollowUpQuestion(q),
            );
          }
        } catch (error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem saving the follow-up question.',
          });
          console.error(error);
        }
      }

      // Creating a new question
      else {
        const lastMaxOrder = Math.max.apply(
          Math,
          this.selectedQuestionnaire.questionnaire_questions
            .filter(
              q =>
                q.follow_up_question_id === question.follow_up_question_id &&
                typeof q.order === 'number',
            )
            .map(q => q.order),
        );

        const isLastEmpty = isEmpty(
          this.selectedQuestionnaire.questionnaire_questions.filter(
            q => q.follow_up_question_id === question.follow_up_question_id,
          ),
        );
        const lastOrder = isLastEmpty ? 1 : lastMaxOrder;

        try {
          const { data } = await API.questionnaireTemplate.createQuestion({
            disable_answer_later: question.disable_answer_later,
            disable_not_applicable: question.disable_not_applicable,
            followUpQuestionId: question.follow_up_question_id,
            number_of_characters: question.number_of_characters,
            optional: question.optional,
            options: question.options,
            order:
              typeof question.order === 'number'
                ? question.order
                : lastOrder
                ? lastOrder + 1
                : null,
            questionnaireTemplateId: this.selectedQuestionnaire.id,
            questionType: question.question_type,
            section_id: question.section_id,
            stack_prior_year: question.stack_prior_year,
            text: question.text,
            triggerForFollowUpQuestion: question.trigger_for_follow_up_question,
          });

          this.selectedQuestionnaire.questionnaire_questions.push(data);

          const hasEntryOptions = Boolean(
            question.question_type === 'entries' &&
              question.entryOptions &&
              question.entryOptions.length,
          );

          // Handle optional 2nd level entry options
          if (hasEntryOptions) {
            await this.asyncForEach(question.entryOptions, entryOption => {
              const genericQuestion = this.generateQuestion(
                entryOption.type,
                null,
                'Template',
                data.section_id,
                data.id,
                entryOption.options,
                entryOption.text,
              );

              return this.saveFollowUpQuestion(genericQuestion);
            });
          }
        } catch (error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem saving the follow-up question.',
          });
          console.error(error);
        }
      }
    },
    async getQuestionnaireTemplate(id) {
      const response = await API.questionnaireTemplate.getQuestionnaireTemplate(
        id,
      );

      this.selectedQuestionnaire = response.data;
      this.mapToTaxSoftware =
        this.showTaxSoftwareMapping &&
        this.selectedQuestionnaire.questionnaire_questions.some(
          q => q.mapped_form && q.mapped_key,
        );

      if (this.mapToTaxSoftware) {
        this.savedForm = this.selectedQuestionnaire.questionnaire_questions
          .filter(q => q.mapped_form)
          .map(q => q.mapped_form)[0];
      }

      this.orderedSections = this.selectedQuestionnaire.sections
        .sort((a, b) => a.order - b.order)
        .map(a => {
          return {
            ...a,
            editing: false,
          };
        });
    },
    async getAllQuestionnaireTemplates(skipLoadingUpdate) {
      if (!skipLoadingUpdate) this.loading = true;

      const response = await API.questionnaireTemplate.getAllQuestionnaireTemplatesInfo();
      this.questionnaireTemplates = response.data.sort((a, b) => b.id - a.id);

      if (!skipLoadingUpdate) this.loading = false;
    },
    async selectMostRecentTemplate() {
      if (this.questionnaireTemplates.length) {
        if (this.active === 1) {
          await this.loadActiveTemplate();
        }
        this.active = 1;
      } else {
        this.active = 0;
      }
    },
    async loadActiveTemplate() {
      this.templateLoading = true;
      await this.getQuestionnaireTemplate(
        this.questionnaireTemplates[this.active - 1].id,
      );
      this.editEngagementLetter = false;
      this.sectionsToInclude = [];
      this.selectedQuestionnaire.sections.forEach(i => {
        this.sectionsToInclude.push(true);
      });
      this.templateLoading = false;
    },
    activateStep(incomingHash) {
      switch (incomingHash) {
        case '#onboarding-customize-questionnaire-messages':
          this.isOnboarding = true;
          break;
        case '':
          this.isOnboarding = false;
          break;
        default:
          this.isOnboarding = false;
          break;
      }
    },
  },
  async created() {
    const { hash } = this.$route;

    this.activateStep(hash);
    this.loading = true;

    await this.getCurrentUserFirm();
    await this.checkStripeIntegration();

    const isUnauthorized = Boolean(
      this.currentUser.firm_access === 'basic' ||
        (this.templateDisabledForFirmGroups &&
          this.currentUser.firm_access === 'group') ||
        (this.templateDisabledForNonAdmins &&
          this.currentUser.firm_access !== 'partner'),
    );

    if (isUnauthorized) {
      this.$store.dispatch('application/setErrorNotification', {
        message: `You do not have access`,
      });
      this.$router.push('/overview');
    }

    try {
      await this.getAllQuestionnaireTemplates(true);
      await this.getQuestionnaireTemplate(this.questionnaireTemplates[0].id);
      await this.getExportableForms();

      if (this.selectedQuestionnaire) {
        this.sectionsToInclude = [];
        this.selectedQuestionnaire.sections.forEach(i => {
          this.sectionsToInclude.push(true);
        });
      }
    } catch (error) {
      this.$store.dispatch('application/setErrorNotification', {
        message: `Something went wrong loading data.`,
      });
      console.error(error);
    } finally {
      this.loading = false;
    }
  },
  watch: {
    active: {
      async handler() {
        if (this.active === 0 || isEmpty(this.questionnaireTemplates)) {
          this.selectedQuestionnaire = this.newQuestionnaireTemplate;
        } else {
          await this.loadActiveTemplate();
        }
      },
      immediate: true,
    },
    searchQuery: {
      handler(val) {
        this.active = 0;
      },
    },
    $route: {
      async handler({ hash }) {
        this.activateStep(hash);
      },
    },
    selectedQuestion: {
      handler() {
        if (this.conditionalSectionOn && this.editSectionDialogOpened) {
          if (
            this.selectedQuestion.question_type === 'boolean' &&
            !['Yes', 'No'].includes(this.selectedSection.trigger_answer)
          ) {
            this.selectedSection.trigger_answer = 'Yes'; // if question is a boolean, and doesn't have the correct set trigger answer to yes
          } else if (
            ['select', 'multiple'].includes(
              this.selectedQuestion.question_type,
            ) &&
            !this.selectedQuestion.options.includes(
              this.selectedSection.trigger_answer,
            )
          ) {
            this.selectedSection.trigger_answer = null; // if question is a select or multiple, and doesn't have the correct set trigger answer to null
          }
        }
      },
    },
    sectionToShow: {
      handler() {
        if (this.conditionalSectionOn && this.editSectionDialogOpened) {
          this.selectedQuestion = null;
        }
      },
    },
  },
};
</script>

<style lang="css" scoped="true">
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.list-group {
  min-height: 20px;
  margin-bottom: 16px !important;
  margin-right: 16px !important;
  margin-left: 0;
}

.list-group > span {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.list-group-item {
  padding: 4px 12px;
  margin-bottom: 0 !important;
  overflow: hidden;

  border: solid 1px #e3e5ef !important;
  border-radius: 4px;
  box-shadow: 0 0 0 2px var(--gray-50) !important;

  cursor: move;
}
.list-group-item i {
  cursor: pointer;
}

.follow-up__question-container,
.additional-follow-up__question-container {
  border-radius: 8px;
  border: 1px solid var(--gray-200);
}

.follow-up__question-container {
  padding: 16px;

  background-color: var(--gray-50);
}
.additional-follow-up__question-container {
  padding: 16px;

  background-color: var(--gray-0);
}

.questionnaire-expansion-header {
  display: flex;
  align-items: center;
}

.questionnaire-expansion-header .client-title {
  flex-grow: 1;
}

.question-chip {
  max-width: calc(90%);
}

.question-chip .v-chip__content {
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  padding-top: 6px;
}

.questionnaire_template_block {
  margin: 0;

  border: 0 solid var(--gray-100) !important;
  box-shadow: 0 0 0 transparent !important;
}

.questionnaire_template_block__icon {
  opacity: 0.5 !important;
}
.questionnaire-templates-container {
  padding-top: 24px;
}

/* NOTE @chris This is to overwrite vuetify defaults, so single checkboxes aren't too far apart. */
.questionnaire-templates-container > div {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.questionnaire_template__options-container {
  display: flex;
  margin-block: 16px;
  gap: 16px;
}

.questionnaire_template__options-container > * {
  flex-grow: unset !important;
}

.questionnaire_template__condition_bar {
  align-items: center;
  display: flex;
  padding: 4px 12px;
  margin-bottom: 12px;
  margin-left: -16px;
  margin-right: -16px;
  margin-top: -16px;

  border-bottom: 1px solid var(--gray-100);
}

.ql-toolbar {
  border: 1px solid red !important;
}

.questionnaire-template-line {
  align-items: center;
  display: flex;
  margin-right: 4px;

  background-color: var(--gray-100);
  border-radius: 4px;
}

.questionnaire-template-line__icon {
  align-items: center;
  display: flex;
  height: 32px;
  justify-content: center;
  min-width: 32px;
  width: 32px;

  color: var(--gray-1000);
}

.questionnaire-template-line__icon i {
  color: currentcolor !important;
}

.questionnaire-template-line__option {
  align-items: center;
  display: flex;
  height: 32px;
  padding: 0 16px;
  justify-content: center;

  background-color: var(--gray-100);
  border-radius: 4px;
  color: var(--gray-800);

  font-size: 14px;
  font-weight: 500;
  line-height: 32px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.questionnaire-template-glossarybox {
  display: flex;
  padding: 6px;

  background-color: var(--gray-100);
  border-radius: 4px;

  line-height: 20px;
}

.questionnaire-template-glossarybox--top {
  background-color: transparent;
}

.questionnaire-template-question {
  padding-top: 12px;

  border-top: 1px solid var(--gray-100);
}

.quill-select-override {
  min-width: 100%;
}

.quill-select-container {
  flex-grow: 1 !important;
  width: 100%;
}

.material-icons-outlined {
  font-size: 20px;
}

.light-gray--text {
  color: var(--gray-1000);
}

.questionnaire-editor__footer {
  display: flex;
  justify-content: space-between;
  padding: 0 20px 20px 24px;
}

/* NOTE @chris Full width editor for custom sections */
.ql-message-input--long {
  margin: -1px;
  width: calc(100% + 2px);
}

.questionnaire_templates_custom_section {
  padding: 20px;

  border-top: 1px solid var(--gray-100);
}

.questionnaire_templates_custom_section__controls {
  /* NOTE @chris Need this for vuetify temp comp. */
  z-index: 1;

  padding: 0 20px 12px 20px;
  margin-bottom: 16px;

  background-color: var(--gray-0);
  border-bottom: 1px solid var(--gray-100);
}

.questionnaire_templates__mainContainer {
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px;

  border-bottom: 1px solid var(--gray-100);
}

.questionnaire_templates__templateItemContainer {
  position: relative;
  left: -16px;
  z-index: 1;

  display: flex;
  gap: 8px;

  overflow: auto;
  padding: 0 24px 12px 16px;
  width: calc(100% + 32px);

  background-color: white;
  box-shadow: inset -24px 0 24px -24px rgba(255, 255, 255, 0.5),
    inset 24px 0 24px -24px rgba(255, 255, 255, 0.5);
  mask-image: linear-gradient(
    to right,
    transparent,
    white 16px,
    white calc(100% - 16px),
    transparent
  );
}

.questionnaire_templates__templateItemEmpty,
.questionnaire_templates__templateItem {
  height: 32px;
  padding: 0 12px;

  background-color: var(--blue-100);
  border-radius: 4px;
  border: 1px solid var(--blue-200);

  cursor: pointer;
  line-height: 32px;
  white-space: nowrap;
}

.questionnaire_templates__templateItemEmpty {
  background-color: var(--gray-50);
  border-color: var(--gray-50);
  color: var(--gray-600);
}

.questionnaire_templates__templateItemSticky {
  position: relative;
}

.questionnaire_templates__templateItemSelected {
  background-color: var(--blue-200);
}

.drag_handle {
  cursor: grab;
}

.drag_handle:active {
  cursor: grabbing;
}
</style>
